import React, { useEffect, useState, useContext, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../App';
import { useVideoCall } from '../contexts/VideoCallContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  ArrowLeft, Loader2, Video, Calendar, Edit2, Plus, X, Users, Trash2, UserPlus,
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import axios from 'axios';
import { toast } from 'sonner';

/**
 * Comité Evangelístico IBA — clon exacto de la mecánica de Sala Docente
 * para Reuniones (Daily.co con invitados + RSVP) y de Actividades
 * Extracurriculares para el tab de Actividades. Se suma una pestaña de
 * Miembros (solo admin) para gestionar quiénes son del comité.
 *
 * Permisos (server-side):
 *   - VER reuniones/actividades: cualquier autenticado
 *   - CREAR/EDITAR/BORRAR: admin O miembro del comité
 *   - GESTIONAR MIEMBROS: solo admin
 */
const ComiteEvangelistico = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { API, user } = useContext(AuthContext);
  const initialTab = searchParams.get('tab') || 'actividades';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [perms, setPerms] = useState(null);

  const switchTab = (t) => {
    setActiveTab(t);
    const next = new URLSearchParams(searchParams);
    next.set('tab', t);
    setSearchParams(next, { replace: true });
  };

  const headersRef = useRef({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/comite/me`, { headers: headersRef.current });
        setPerms(r.data);
      } catch (_e) {
        setPerms({ es_admin: false, es_miembro: false, puede_crear: false });
      }
    })();
  }, [API]);

  if (!perms) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-rose-600" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 max-w-5xl" data-testid="comite-evangelistico-page">
      {/* Header */}
      <div className="mb-4 flex items-center gap-3">
        <button
          onClick={() => navigate('/app')}
          className="p-1.5 rounded-full hover:bg-gray-100 text-gray-600"
          data-testid="comite-back"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <i className="fas fa-hands-praying text-rose-600 text-2xl"></i>
              <span className="bg-gradient-to-br from-rose-600 to-amber-600 bg-clip-text text-transparent">
                Comité Evangelístico IBA
              </span>
            </h1>
            {perms.es_miembro && (
              <Badge className="bg-rose-100 text-rose-700 border-0">Miembro</Badge>
            )}
            {perms.es_admin && (
              <Badge className="bg-purple-100 text-purple-700 border-0">Admin</Badge>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-0.5">
            Reuniones, actividades evangelísticas y coordinación del comité
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={switchTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-flex" data-testid="comite-tabs">
          <TabsTrigger value="actividades" data-testid="tab-comite-actividades">
            <i className="fas fa-star mr-2"></i> Actividades
          </TabsTrigger>
          <TabsTrigger value="en-vivo" data-testid="tab-comite-en-vivo">
            <span className="relative flex items-center mr-2">
              <span className="w-2 h-2 bg-red-500 rounded-full" />
              <span className="absolute w-2 h-2 bg-red-500 rounded-full animate-ping" />
            </span>
            En Vivo
          </TabsTrigger>
          <TabsTrigger value="reuniones" data-testid="tab-comite-reuniones">
            <Video className="w-4 h-4 mr-2" /> Reuniones
          </TabsTrigger>
          <TabsTrigger value="miembros" data-testid="tab-comite-miembros">
            <Users className="w-4 h-4 mr-2" /> Miembros
          </TabsTrigger>
        </TabsList>

        <TabsContent value="actividades" className="mt-6">
          <ActividadesTabComite API={API} user={user} perms={perms} />
        </TabsContent>
        <TabsContent value="en-vivo" className="mt-6">
          <EnVivoTabComite API={API} user={user} />
        </TabsContent>
        <TabsContent value="reuniones" className="mt-6">
          <ReunionesTabComite API={API} user={user} perms={perms} />
        </TabsContent>
        <TabsContent value="miembros" className="mt-6">
          <MiembrosTabComite API={API} user={user} perms={perms} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ============================================================================
// REUNIONES TAB — clon de Sala Docente con invitados + RSVP + Daily
// ============================================================================
const ReunionesTabComite = ({ API, user, perms }) => {
  const [reuniones, setReuniones] = useState([]);
  const [usuariosDisponibles, setUsuariosDisponibles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingReunion, setEditingReunion] = useState(null);
  const [filter, setFilter] = useState('proximas');
  const [joiningId, setJoiningId] = useState(null);
  const { openCall } = useVideoCall();
  const headersRef = useRef({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const rRes = await axios.get(`${API}/comite/reuniones?estado=${filter}`, { headers: headersRef.current });
      setReuniones(rRes.data.reuniones || []);
      if (perms.puede_crear) {
        try {
          const pRes = await axios.get(`${API}/comite/disponibles`, { headers: headersRef.current });
          setUsuariosDisponibles(pRes.data.usuarios || []);
        } catch (_e) { /* ignore */ }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [API, filter, perms.puede_crear]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleJoin = async (rId) => {
    setJoiningId(rId);
    try {
      const r = await axios.get(`${API}/comite/reuniones/${rId}/join`, { headers: headersRef.current });
      openCall({ url: r.data.room_url, channelId: null });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo unir a la reunión');
    } finally {
      setJoiningId(null);
    }
  };

  const handleCancel = async (rId) => {
    if (!window.confirm('¿Cancelar esta reunión? Se notificará automáticamente (push + email) a los invitados.')) return;
    try {
      const r = await axios.delete(`${API}/comite/reuniones/${rId}`, { headers: headersRef.current });
      const n = r?.data?.notificados ?? 0;
      toast.success(n > 0 ? `Reunión cancelada. Se notificó a ${n} invitado(s)` : 'Reunión cancelada');
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cancelar');
    }
  };

  const handleResendCancelacion = async (rId) => {
    if (!window.confirm('¿Reenviar la notificación de cancelación (push + email) a todos los invitados?')) return;
    try {
      const r = await axios.post(`${API}/comite/reuniones/${rId}/notificar-cancelacion`, {}, { headers: headersRef.current });
      const n = r?.data?.notificados ?? 0;
      toast.success(n > 0 ? `Notificación reenviada a ${n} invitado(s)` : 'Sin destinatarios para notificar');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo reenviar la notificación');
    }
  };

  const handleRSVP = async (rId, status, reason) => {
    try {
      await axios.post(
        `${API}/comite/reuniones/${rId}/rsvp`,
        { status, reason: reason || '' },
        { headers: headersRef.current }
      );
      const msg = { aceptado: '✅ Confirmaste tu asistencia', tal_vez: '🤔 Marcada como tentativa', rechazado: '❌ Marcada como no asistirá' }[status];
      toast.success(msg || 'Respuesta enviada');
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo enviar la respuesta');
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-1 bg-gray-100 p-0.5 rounded-lg">
          <button
            onClick={() => setFilter('proximas')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md ${
              filter === 'proximas' ? 'bg-white shadow-sm text-rose-700' : 'text-gray-600'
            }`}
            data-testid="comite-filter-proximas"
          >
            Próximas
          </button>
          <button
            onClick={() => setFilter('pasadas')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md ${
              filter === 'pasadas' ? 'bg-white shadow-sm text-rose-700' : 'text-gray-600'
            }`}
            data-testid="comite-filter-pasadas"
          >
            Pasadas
          </button>
          <button
            onClick={() => setFilter('canceladas')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md ${
              filter === 'canceladas' ? 'bg-white shadow-sm text-red-700' : 'text-gray-600'
            }`}
            data-testid="comite-filter-canceladas"
          >
            Canceladas
          </button>
        </div>
        {perms.puede_crear && (
          <Button onClick={() => setShowCreate(true)} size="sm" className="bg-rose-600 hover:bg-rose-700" data-testid="btn-nueva-reunion-comite">
            <Video className="w-4 h-4 mr-1" /> Nueva reunión
          </Button>
        )}
      </div>

      {loading ? (
        <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-rose-600" /></div>
      ) : reuniones.length === 0 ? (
        <Card className="bg-gray-50 border-dashed">
          <CardContent className="p-8 text-center text-gray-500">
            <Video className="w-10 h-10 mx-auto text-gray-300 mb-2" />
            <p className="text-sm">
              {filter === 'proximas' ? 'No hay reuniones próximas.' : filter === 'canceladas' ? 'No hay reuniones canceladas.' : 'No hay reuniones pasadas.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {reuniones.map((r) => (
            <ReunionCardComite
              key={r.id}
              reunion={r}
              currentUserId={user?.id}
              currentUserRole={user?.rol}
              joining={joiningId === r.id}
              onJoin={() => handleJoin(r.id)}
              onCancel={() => handleCancel(r.id)}
              onEdit={() => setEditingReunion(r)}
              onRSVP={handleRSVP}
              isPast={filter === 'pasadas'}
              isCancelled={filter === 'canceladas'}
              onResendCancelacion={() => handleResendCancelacion(r.id)}
              canManage={perms.puede_crear}
              API={API}
            />
          ))}
        </div>
      )}

      {(showCreate || editingReunion) && (
        <CreateReunionModalComite
          isOpen
          onClose={() => { setShowCreate(false); setEditingReunion(null); }}
          onCreated={loadAll}
          API={API}
          usuarios={usuariosDisponibles}
          currentUserId={user?.id}
          editing={editingReunion}
        />
      )}
    </div>
  );
};

const RSVP_STYLES = {
  aceptado: { bg: 'bg-emerald-500', ring: 'ring-emerald-400', icon: '✓', label: 'Asistirá', textColor: 'text-emerald-700', bgLight: 'bg-emerald-50' },
  tal_vez: { bg: 'bg-amber-500', ring: 'ring-amber-400', icon: '?', label: 'Tal vez', textColor: 'text-amber-700', bgLight: 'bg-amber-50' },
  rechazado: { bg: 'bg-red-500', ring: 'ring-red-400', icon: '✕', label: 'No asistirá', textColor: 'text-red-700', bgLight: 'bg-red-50' },
  pendiente: { bg: 'bg-gray-300', ring: 'ring-gray-300', icon: '⏳', label: 'Sin responder', textColor: 'text-gray-600', bgLight: 'bg-gray-50' },
};

const InvitadoAvatarComite = ({ invitado }) => {
  const status = invitado.status || 'pendiente';
  const sty = RSVP_STYLES[status] || RSVP_STYLES.pendiente;
  return (
    <div className="relative shrink-0" title={`${invitado.nombre} — ${sty.label}${invitado.reason ? ` (${invitado.reason})` : ''}`}>
      {invitado.image ? (
        <img src={invitado.image} alt={invitado.nombre} className={`w-8 h-8 rounded-full object-cover ring-2 ${sty.ring}`} />
      ) : (
        <div className={`w-8 h-8 rounded-full bg-rose-200 text-rose-700 text-xs font-bold flex items-center justify-center ring-2 ${sty.ring}`}>
          {(invitado.nombre || '?').slice(0, 1).toUpperCase()}
        </div>
      )}
      <span className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 ${sty.bg} text-white text-[8px] font-bold rounded-full flex items-center justify-center border-2 border-white`}>
        {sty.icon}
      </span>
    </div>
  );
};

const ReunionCardComite = ({ reunion, currentUserId, currentUserRole, joining, onJoin, onCancel, onEdit, onRSVP, isPast, isCancelled, onResendCancelacion, canManage, API }) => {
  const fecha = reunion.fecha_hora_inicio ? new Date(reunion.fecha_hora_inicio) : null;
  const inviCount = (reunion.invitados_ids || []).length;
  const puedeModificar =
    !isPast &&
    reunion.estado === 'activa' &&
    (reunion.organizador_id === currentUserId || currentUserRole === 'admin');
  const miRsvp = reunion.mi_rsvp;
  const miStatus = miRsvp?.status || (reunion.invitados_ids?.includes(currentUserId) ? 'pendiente' : null);
  const puedoRsvp = !isPast && reunion.estado === 'activa' && !reunion.soy_organizador && miStatus;
  const resumen = reunion.rsvps_resumen || {};
  const invitadosConRsvp = reunion.invitados_con_rsvp || [];

  const [showRejectReason, setShowRejectReason] = useState(false);
  const [rejectReason, setRejectReason] = useState(miStatus === 'rechazado' ? (miRsvp?.reason || '') : '');
  const [rsvpSubmitting, setRsvpSubmitting] = useState(false);

  // Reenvío de invitación (mismo patrón que actividades).
  // El organizador y los admins pueden reenviar; sólo si la reunión está activa
  // y hay invitados que aún no aceptaron.
  const esAdminOOrganizador = currentUserRole === 'admin' || reunion.organizador_id === currentUserId;
  const pendientesParaReenvio = invitadosConRsvp.filter((inv) => (inv.status || 'pendiente') !== 'aceptado' && inv.id !== reunion.organizador_id);
  const puedeReenviar = !isPast && reunion.estado === 'activa' && esAdminOOrganizador && pendientesParaReenvio.length > 0;
  const [showReenviar, setShowReenviar] = useState(false);
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [reenviando, setReenviando] = useState(false);

  const toggleSel = (id) => setSeleccionados((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const seleccionarTodosPendientes = () => setSeleccionados(new Set(pendientesParaReenvio.map((i) => i.id)));
  const limpiarSeleccion = () => setSeleccionados(new Set());

  const handleReenviar = async () => {
    if (seleccionados.size === 0) {
      toast.error('Seleccioná al menos un invitado para reenviar');
      return;
    }
    setReenviando(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const r = await axios.post(
        `${API}/comite/reuniones/${reunion.id}/reenviar-invitacion`,
        { user_ids: Array.from(seleccionados) },
        { headers }
      );
      toast.success(`Notificación reenviada a ${r.data?.enviados || seleccionados.size} invitado(s)`);
      setShowReenviar(false);
      setSeleccionados(new Set());
    } catch (e) {
      const msg = e?.response?.data?.detail || 'No se pudo reenviar la invitación';
      toast.error(msg);
    } finally {
      setReenviando(false);
    }
  };

  const respond = async (status) => {
    if (rsvpSubmitting) return;
    if (status === 'rechazado' && !showRejectReason) {
      setShowRejectReason(true);
      return;
    }
    setRsvpSubmitting(true);
    try {
      await onRSVP(reunion.id, status, status === 'rechazado' ? rejectReason.trim() : '');
      setShowRejectReason(false);
    } finally {
      setRsvpSubmitting(false);
    }
  };

  return (
    <Card className="border shadow-sm border-gray-100" data-testid={`comite-reunion-card-${reunion.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-gray-800 truncate">{reunion.titulo}</h3>
              {reunion.soy_organizador && <Badge variant="outline" className="text-[10px]">Organizo yo</Badge>}
              {reunion.estado === 'cancelada' && <Badge className="bg-red-100 text-red-700 border-0 text-[10px]">Cancelada</Badge>}
              {miStatus && !reunion.soy_organizador && (
                <Badge className={`${RSVP_STYLES[miStatus].bgLight} ${RSVP_STYLES[miStatus].textColor} border-0 text-[10px]`}>
                  {RSVP_STYLES[miStatus].icon} {RSVP_STYLES[miStatus].label}
                </Badge>
              )}
            </div>
            {fecha && (
              <p className="text-xs text-gray-500 mt-0.5">
                📅 {fecha.toLocaleDateString('es-UY', { timeZone: 'America/Montevideo' })} · {fecha.toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Montevideo' })} · ⏱ {reunion.duracion_minutos || 60} min
              </p>
            )}
            <p className="text-xs text-gray-400 mt-0.5">Organiza: {reunion.organizador_nombre} · {inviCount} invitados</p>
            {reunion.descripcion && (
              <p className="text-sm text-gray-600 mt-2 whitespace-pre-wrap">{reunion.descripcion}</p>
            )}

            {invitadosConRsvp.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <div className="flex items-center gap-3 flex-wrap text-xs mb-2">
                  <span className="font-semibold text-gray-700">Asistencia:</span>
                  {resumen.aceptado > 0 && (
                    <span className="flex items-center gap-1 text-emerald-700"><span className="w-2 h-2 rounded-full bg-emerald-500" />{resumen.aceptado} asistirá{resumen.aceptado > 1 ? 'n' : ''}</span>
                  )}
                  {resumen.tal_vez > 0 && (
                    <span className="flex items-center gap-1 text-amber-700"><span className="w-2 h-2 rounded-full bg-amber-500" />{resumen.tal_vez} tal vez</span>
                  )}
                  {resumen.rechazado > 0 && (
                    <span className="flex items-center gap-1 text-red-700"><span className="w-2 h-2 rounded-full bg-red-500" />{resumen.rechazado} no podrá{resumen.rechazado > 1 ? 'n' : ''}</span>
                  )}
                  {resumen.pendiente > 0 && (
                    <span className="flex items-center gap-1 text-gray-500"><span className="w-2 h-2 rounded-full bg-gray-400" />{resumen.pendiente} sin responder</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {invitadosConRsvp.map((inv) => (
                    <InvitadoAvatarComite key={inv.id} invitado={inv} />
                  ))}
                </div>
                {puedeReenviar && (
                  <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded-md flex items-center justify-between gap-2 flex-wrap" data-testid={`comite-reunion-reenviar-block-${reunion.id}`}>
                    <span className="text-xs text-blue-900">
                      <strong>{pendientesParaReenvio.length}</strong> invitado(s) aún no aceptaron.
                    </span>
                    <Button
                      size="sm"
                      className="h-7 px-2 text-xs bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={() => { setShowReenviar(true); seleccionarTodosPendientes(); }}
                      data-testid={`comite-reunion-reenviar-btn-${reunion.id}`}
                    >
                      <i className="fas fa-paper-plane mr-1"></i> Reenviar notificación
                    </Button>
                  </div>
                )}
                {/* Botón de reenvío de notificación de CANCELACIÓN
                    (solo si el tab activo es "Canceladas" y soy organizador/admin) */}
                {isCancelled && esAdminOOrganizador && onResendCancelacion && (
                  <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-md flex items-center justify-between gap-2 flex-wrap" data-testid={`comite-reunion-renotificar-cancelacion-block-${reunion.id}`}>
                    <span className="text-xs text-red-900">
                      Esta reunión fue cancelada. Podés reenviar el aviso a los invitados.
                    </span>
                    <Button
                      size="sm"
                      className="h-7 px-2 text-xs bg-red-600 hover:bg-red-700 text-white"
                      onClick={onResendCancelacion}
                      data-testid={`comite-reunion-renotificar-cancelacion-btn-${reunion.id}`}
                    >
                      <i className="fas fa-paper-plane mr-1"></i> Reenviar aviso de cancelación
                    </Button>
                  </div>
                )}
              </div>
            )}

            {puedoRsvp && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <p className="text-xs font-semibold text-gray-700 mb-2">
                  {miStatus === 'pendiente' ? '¿Vas a asistir?' : 'Cambiar mi respuesta:'}
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => respond('aceptado')}
                    disabled={rsvpSubmitting}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold ${
                      miStatus === 'aceptado' ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200'
                    }`}
                    data-testid={`comite-rsvp-accept-${reunion.id}`}
                  >
                    ✅ Acepto
                  </button>
                  <button
                    onClick={() => respond('tal_vez')}
                    disabled={rsvpSubmitting}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold ${
                      miStatus === 'tal_vez' ? 'bg-amber-500 text-white' : 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'
                    }`}
                    data-testid={`comite-rsvp-maybe-${reunion.id}`}
                  >
                    🤔 Tal vez
                  </button>
                  <button
                    onClick={() => respond('rechazado')}
                    disabled={rsvpSubmitting}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold ${
                      miStatus === 'rechazado' ? 'bg-red-600 text-white' : 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200'
                    }`}
                    data-testid={`comite-rsvp-reject-${reunion.id}`}
                  >
                    ❌ No acepto
                  </button>
                </div>
                {showRejectReason && (
                  <div className="mt-2 space-y-2 bg-red-50 border border-red-200 rounded-md p-2">
                    <textarea
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="Razón (opcional)..."
                      rows={2}
                      maxLength={500}
                      className="w-full text-xs border border-red-200 rounded p-1.5 focus:outline-none resize-none"
                    />
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => { setShowRejectReason(false); setRejectReason(''); }} className="text-xs text-gray-600 hover:text-gray-800" disabled={rsvpSubmitting}>
                        Cancelar
                      </button>
                      <button onClick={() => respond('rechazado')} disabled={rsvpSubmitting}
                        className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs font-semibold">
                        {rsvpSubmitting ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Confirmar rechazo'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            {!isPast && reunion.estado === 'activa' && miStatus !== 'rechazado' && (
              <Button onClick={onJoin} disabled={joining} size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid={`comite-join-reunion-${reunion.id}`}>
                {joining ? <Loader2 className="w-4 h-4 animate-spin" /> : <Video className="w-4 h-4 mr-1" />}
                Entrar
              </Button>
            )}
            {puedeModificar && (
              <>
                <Button onClick={onEdit} size="sm" variant="outline" className="border-rose-200 text-rose-600 hover:bg-rose-50" data-testid={`comite-edit-reunion-${reunion.id}`}>
                  <Edit2 className="w-3.5 h-3.5 mr-1" /> Editar
                </Button>
                {/* Invitar externo: renueva la sala Daily.co y copia el link
                    listo para WhatsApp/email. La persona NO necesita cuenta
                    en IBA Connect — solo abre el link y pone su nombre. */}
                {!isPast && !isCancelled && reunion.estado === 'activa' && reunion.daily_room_url && (
                  <Button
                    onClick={async () => {
                      let urlFinal = reunion.daily_room_url;
                      try {
                        const r = await axios.post(`${API}/comite/reuniones/${reunion.id}/renovar-link`, {});
                        if (r?.data?.url) urlFinal = r.data.url;
                      } catch (_e) { /* sigue con URL original */ }
                      const fecha = reunion.fecha_hora_inicio ? new Date(reunion.fecha_hora_inicio) : null;
                      const fechaStr = fecha ? `${fecha.toLocaleDateString('es-UY', { weekday: 'long', day: '2-digit', month: 'long', timeZone: 'America/Montevideo' })} a las ${fecha.toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Montevideo' })} (hora Uruguay)` : '';
                      const texto = `Estás invitado a la reunión del Comité Evangelístico IBA Uruguay:\n\n📌 ${reunion.titulo}\n${fechaStr ? `📅 ${fechaStr}\n` : ''}\n🔗 Unirse: ${urlFinal}\n\nNo necesitás cuenta en IBA Connect. Solo abrí el link y poné tu nombre al entrar.`;
                      try {
                        if (navigator.share && /Mobi|Android|iPhone/i.test(navigator.userAgent)) {
                          await navigator.share({ title: `Reunión: ${reunion.titulo}`, text: texto });
                          return;
                        }
                        await navigator.clipboard.writeText(texto);
                        toast.success('Link de invitado copiado y renovado. Pegalo donde quieras.');
                      } catch (_e) {
                        window.prompt('Copiá el siguiente texto para invitar:', texto);
                      }
                    }}
                    size="sm"
                    variant="outline"
                    className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                    title="Copiar link para invitar a alguien que no tiene cuenta en IBA Connect"
                    data-testid={`comite-invitar-externo-reunion-${reunion.id}`}
                  >
                    <i className="fas fa-share-alt mr-1.5" /> Invitar externo
                  </Button>
                )}
                <Button onClick={onCancel} size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`comite-cancel-reunion-${reunion.id}`}>
                  Cancelar
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>

      {/* Modal Reenviar invitación a invitados de la reunión */}
      {showReenviar && (
        <Dialog open={showReenviar} onOpenChange={(open) => { if (!open) { setShowReenviar(false); limpiarSeleccion(); } }}>
          <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="comite-reunion-reenviar-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center text-blue-700">
                <i className="fas fa-paper-plane mr-2"></i> Reenviar notificación
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <p className="text-sm text-gray-600">
                Seleccioná a quién querés reenviar la invitación a la reunión. Los que ya aceptaron no aparecen.
              </p>
              <div className="flex justify-between items-center text-xs">
                <span className="font-semibold text-gray-700">{seleccionados.size} de {pendientesParaReenvio.length} seleccionado(s)</span>
                <div className="flex gap-2">
                  <button onClick={seleccionarTodosPendientes} className="text-blue-600 hover:text-blue-800 font-semibold" data-testid="comite-reunion-reenviar-seleccionar-todos">Todos</button>
                  <span className="text-gray-300">|</span>
                  <button onClick={limpiarSeleccion} className="text-gray-600 hover:text-gray-800 font-semibold" data-testid="comite-reunion-reenviar-limpiar">Ninguno</button>
                </div>
              </div>
              <div className="space-y-1 max-h-[40vh] overflow-y-auto border border-gray-200 rounded-md p-2 bg-gray-50">
                {pendientesParaReenvio.map((inv) => {
                  const sty = RSVP_STYLES[inv.status || 'pendiente'] || RSVP_STYLES.pendiente;
                  const checked = seleccionados.has(inv.id);
                  return (
                    <label
                      key={inv.id}
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-white ${checked ? 'bg-blue-50 border border-blue-300' : 'border border-transparent'}`}
                      data-testid={`comite-reunion-reenviar-item-${inv.id}`}
                    >
                      <input type="checkbox" checked={checked} onChange={() => toggleSel(inv.id)} className="w-4 h-4 accent-blue-600" />
                      {inv.image ? (
                        <img src={inv.image} alt={inv.nombre} className="w-7 h-7 rounded-full object-cover" />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-rose-500 to-amber-500 flex items-center justify-center text-white text-xs font-bold">
                          {(inv.nombre || '?').slice(0, 1).toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{inv.nombre}</p>
                        <p className="text-[11px] text-gray-500">
                          <span className={sty.textColor}>{sty.icon} {sty.label}</span>
                          {inv.reason ? ` · ${inv.reason}` : ''}
                        </p>
                      </div>
                    </label>
                  );
                })}
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setShowReenviar(false); limpiarSeleccion(); }}
                  disabled={reenviando}
                  data-testid="comite-reunion-reenviar-cancel"
                >
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={handleReenviar}
                  disabled={reenviando || seleccionados.size === 0}
                  data-testid="comite-reunion-reenviar-confirm"
                >
                  {reenviando ? (
                    <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Enviando...</>
                  ) : (
                    <><i className="fas fa-paper-plane mr-1"></i> Reenviar a {seleccionados.size}</>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
};

const CreateReunionModalComite = ({ isOpen, onClose, onCreated, API, usuarios, currentUserId, editing }) => {
  // Convierte una Date a string formato datetime-local "YYYY-MM-DDTHH:mm"
  // representando la hora de URUGUAY (America/Montevideo), sin importar la
  // zona horaria del navegador del usuario.
  const toMontevideoLocalInput = (d) => {
    try {
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Montevideo',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false,
      }).formatToParts(d);
      const get = (t) => parts.find(p => p.type === t)?.value || '00';
      return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
    } catch (_e) {
      return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    }
  };
  const initialFecha = useMemo(() => {
    if (editing?.fecha_hora_inicio) {
      try {
        return toMontevideoLocalInput(new Date(editing.fecha_hora_inicio));
      } catch (_e) { /* ignore */ }
    }
    const d = new Date();
    d.setHours(d.getHours() + 1, 0, 0, 0);
    return toMontevideoLocalInput(d);
  }, [editing]);

  const [titulo, setTitulo] = useState(editing?.titulo || '');
  const [descripcion, setDescripcion] = useState(editing?.descripcion || '');
  const [fecha, setFecha] = useState(initialFecha);
  const [duracion, setDuracion] = useState(editing?.duracion_minutos || 60);
  const [invitados, setInvitados] = useState(new Set(editing?.invitados_ids || []));
  const [invitarTodos, setInvitarTodos] = useState(!editing);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  const [rolFiltro, setRolFiltro] = useState('todos');
  const headersRef = useRef({ Authorization: `Bearer ${localStorage.getItem('token')}` });
  const isEdit = !!editing;

  const rolChip = (rol) => {
    const r = (rol || '').toLowerCase();
    if (r === 'profesor') return 'bg-blue-100 text-blue-700 border-blue-300';
    if (r === 'estudiante') return 'bg-emerald-100 text-emerald-700 border-emerald-300';
    if (r === 'admin') return 'bg-purple-100 text-purple-700 border-purple-300';
    if (r === 'pastor') return 'bg-amber-100 text-amber-700 border-amber-300';
    if (r === 'graduado') return 'bg-cyan-100 text-cyan-700 border-cyan-300';
    return 'bg-gray-100 text-gray-600 border-gray-300';
  };

  const conteoPorRol = useMemo(() => {
    const acc = { profesor: 0, estudiante: 0, admin: 0, pastor: 0, graduado: 0 };
    usuarios.forEach((p) => { if (p.id !== currentUserId) acc[p.rol] = (acc[p.rol] || 0) + 1; });
    return acc;
  }, [usuarios, currentUserId]);

  const usuariosFiltrados = useMemo(() => {
    const q = search.toLowerCase().trim();
    return usuarios
      .filter((p) => p.id !== currentUserId)
      .filter((p) => rolFiltro === 'todos' || (p.rol || '').toLowerCase() === rolFiltro)
      .filter((p) => !q || (p.nombre || '').toLowerCase().includes(q) || (p.email || '').toLowerCase().includes(q));
  }, [usuarios, currentUserId, search, rolFiltro]);

  const toggle = (id) => {
    setInvitados((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const submit = async () => {
    if (titulo.trim().length < 3) { toast.error('Título muy corto'); return; }
    if (!fecha) { toast.error('Falta fecha/hora'); return; }
    setSubmitting(true);
    try {
      const body = {
        titulo: titulo.trim(),
        descripcion: descripcion.trim(),
        // Enviar la hora datetime-local TAL CUAL (sin conversión a UTC).
        // El backend la interpreta como hora de Uruguay (America/Montevideo)
        // y la convierte a UTC para almacenar. Esto evita que la hora se
        // desfase según la zona horaria del navegador del usuario.
        fecha_hora_inicio: fecha,
        duracion_minutos: Number(duracion) || 60,
        invitados: invitarTodos ? [] : Array.from(invitados),
      };
      if (isEdit) {
        await axios.patch(`${API}/comite/reuniones/${editing.id}`, body, { headers: headersRef.current });
        toast.success('Reunión actualizada');
      } else {
        await axios.post(`${API}/comite/reuniones`, body, { headers: headersRef.current });
        toast.success('Reunión creada — los invitados serán notificados');
      }
      onCreated?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar reunión');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="comite-create-reunion-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto">
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            <Video className="w-5 h-5 text-rose-600" /> {isEdit ? 'Editar Reunión' : 'Nueva Reunión del Comité'}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Título *</label>
            <input
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              maxLength={140}
              placeholder="Reunión semanal del comité"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-rose-500"
              data-testid="comite-reunion-titulo"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Fecha y hora *</label>
              <input
                type="datetime-local"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-rose-500"
                data-testid="comite-reunion-fecha"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Duración (minutos)</label>
              <select
                value={duracion}
                onChange={(e) => setDuracion(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-rose-500"
              >
                <option value={15}>15 min</option>
                <option value={30}>30 min</option>
                <option value={45}>45 min</option>
                <option value={60}>60 min</option>
                <option value={90}>1h 30 min</option>
                <option value={120}>2 horas</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Descripción (opcional)</label>
            <textarea
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              maxLength={500}
              rows={2}
              placeholder="Agenda, temas a tratar, link de material, etc."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-rose-500"
            />
          </div>
          <div>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input
                type="checkbox"
                checked={invitarTodos}
                onChange={(e) => setInvitarTodos(e.target.checked)}
                className="w-4 h-4 accent-rose-600"
              />
              <span className="font-medium text-gray-700">Invitar a todos los miembros del comité + administradores</span>
            </label>
            <p className="text-[11px] text-gray-400 ml-6 mt-0.5">
              Desmarcá esto para elegir invitados manualmente (incluyendo estudiantes y profesores fuera del comité).
            </p>
            {!invitarTodos && (
              <div className="mt-2 border border-gray-200 rounded-lg max-h-72 overflow-y-auto">
                <div className="sticky top-0 bg-white px-3 py-2 border-b space-y-2 z-10">
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar por nombre o email..."
                    className="w-full text-sm px-2 py-1 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-rose-500"
                  />
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      { key: 'todos', label: 'Todos', count: usuarios.length - (usuarios.some((p) => p.id === currentUserId) ? 1 : 0), cls: 'bg-gray-100 text-gray-700 border-gray-300' },
                      { key: 'admin', label: 'Admins', count: conteoPorRol.admin || 0, cls: 'bg-purple-100 text-purple-700 border-purple-300' },
                      { key: 'profesor', label: 'Profesores', count: conteoPorRol.profesor || 0, cls: 'bg-blue-100 text-blue-700 border-blue-300' },
                      { key: 'estudiante', label: 'Estudiantes', count: conteoPorRol.estudiante || 0, cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
                      { key: 'pastor', label: 'Pastores', count: conteoPorRol.pastor || 0, cls: 'bg-amber-100 text-amber-700 border-amber-300' },
                      { key: 'graduado', label: 'Graduados', count: conteoPorRol.graduado || 0, cls: 'bg-cyan-100 text-cyan-700 border-cyan-300' },
                    ].filter((c) => c.key === 'todos' || c.count > 0).map((c) => (
                      <button
                        key={c.key}
                        type="button"
                        onClick={() => setRolFiltro(c.key)}
                        className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${rolFiltro === c.key ? `${c.cls} ring-2 ring-offset-1 ring-rose-400` : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                      >
                        {c.label} ({c.count})
                      </button>
                    ))}
                  </div>
                </div>
                {usuariosFiltrados.length === 0 && (
                  <p className="px-3 py-4 text-sm text-gray-400 text-center">Sin resultados</p>
                )}
                {usuariosFiltrados.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0">
                    <input
                      type="checkbox"
                      checked={invitados.has(p.id)}
                      onChange={() => toggle(p.id)}
                      className="w-4 h-4 accent-rose-600"
                    />
                    {p.image ? (
                      <img src={p.image} alt={p.nombre} className="w-7 h-7 rounded-full object-cover shrink-0" />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-rose-500 to-amber-500 flex items-center justify-center text-white text-[10px] font-bold shrink-0">
                        {(p.nombre || '?').slice(0, 1).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate flex items-center gap-1.5">
                        <span className="truncate">{p.nombre}</span>
                        <span className={`text-[9px] uppercase font-bold px-1 py-0 rounded border ${rolChip(p.rol)}`}>{p.rol}</span>
                      </p>
                      <p className="text-xs text-gray-400 truncate">{p.email}</p>
                    </div>
                  </label>
                ))}
                <p className="sticky bottom-0 px-3 py-2 text-xs text-gray-500 border-t bg-gray-50">
                  {invitados.size} seleccionado(s)
                </p>
              </div>
            )}
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t px-5 py-3 flex flex-wrap justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
            Cancelar
          </button>
          <Button
            onClick={submit}
            disabled={submitting || titulo.trim().length < 3}
            className="bg-rose-600 hover:bg-rose-700"
            data-testid="comite-reunion-submit-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <i className="fas fa-bell mr-1.5" />}
            {isEdit ? 'Guardar cambios' : 'Crear reunión'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// ACTIVIDADES TAB — Diseño clonado de ActividadesExtracurriculares
// ============================================================================
const ActividadesTabComite = ({ API, user, perms }) => {
  const [actividades, setActividades] = useState([]);
  const [usuariosDisponibles, setUsuariosDisponibles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('activa');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const { openCall } = useVideoCall();
  const headersRef = useRef({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/comite/actividades`, { headers: headersRef.current });
      setActividades(r.data || []);
      if (perms.puede_crear) {
        try {
          const pRes = await axios.get(`${API}/comite/disponibles`, { headers: headersRef.current });
          setUsuariosDisponibles(pRes.data.usuarios || []);
        } catch (_e) { /* ignore */ }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [API, perms.puede_crear]);

  useEffect(() => { load(); }, [load]);

  // Si el detalle abierto ya no existe o cambió (ej. transmisión activada), refrescarlo
  useEffect(() => {
    if (!detalle) return;
    const updated = actividades.find((a) => a.id === detalle.id);
    if (updated && updated !== detalle) setDetalle(updated);
  }, [actividades, detalle]);

  const filtered = useMemo(() => {
    return (actividades || []).filter((a) => (a.estado || 'activa') === filtro);
  }, [actividades, filtro]);

  const handleIniciarTransmision = async (id) => {
    try {
      const r = await axios.post(`${API}/comite/actividades/${id}/transmision/iniciar`, {}, { headers: headersRef.current });
      toast.success('🔴 Transmisión iniciada. Los miembros recibirán una notificación.');
      openCall({
        url: r.data.broadcaster_url,
        channelId: null,
        liveChat: r.data.chat_channel_id ? {
          channelId: r.data.chat_channel_id,
          channelType: r.data.chat_channel_type || 'livestream',
        } : null,
      });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo iniciar la transmisión');
    }
  };

  const handleFinalizarTransmision = async (id) => {
    if (!window.confirm('¿Finalizar la transmisión en vivo?')) return;
    try {
      await axios.post(`${API}/comite/actividades/${id}/transmision/finalizar`, {}, { headers: headersRef.current });
      toast.success('Transmisión finalizada');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo finalizar');
    }
  };

  const handleVerTransmision = async (id) => {
    try {
      const r = await axios.get(`${API}/comite/actividades/${id}/transmision/unirse`, { headers: headersRef.current });
      const url = r.data.es_emisor && r.data.broadcaster_url ? r.data.broadcaster_url : r.data.viewer_url;
      openCall({
        url,
        channelId: null,
        liveChat: r.data.chat_channel_id ? {
          channelId: r.data.chat_channel_id,
          channelType: r.data.chat_channel_type || 'livestream',
        } : null,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo unir a la transmisión');
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('¿Cancelar esta actividad?')) return;
    try {
      await axios.delete(`${API}/comite/actividades/${id}`, { headers: headersRef.current });
      toast.success('Actividad cancelada');
      setDetalle(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cancelar');
    }
  };

  const handleEstado = async (id, nuevo) => {
    try {
      await axios.patch(`${API}/comite/actividades/${id}`, { estado: nuevo }, { headers: headersRef.current });
      toast.success(`Actividad marcada como ${nuevo}`);
      setDetalle(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    }
  };

  const tipoColor = (tipo) => {
    const c = {
      evangelismo: 'bg-rose-100 text-rose-800',
      campana: 'bg-amber-100 text-amber-800',
      visita: 'bg-pink-100 text-pink-800',
      retiro: 'bg-purple-100 text-purple-800',
      capacitacion: 'bg-blue-100 text-blue-800',
      evento: 'bg-emerald-100 text-emerald-800',
      devocional: 'bg-indigo-100 text-indigo-800',
    };
    return c[tipo] || 'bg-gray-100 text-gray-800';
  };

  const tipoLabel = (tipo) => ({
    evangelismo: 'Evangelismo',
    campana: 'Campaña',
    visita: 'Visita a Iglesias',
    retiro: 'Retiro',
    capacitacion: 'Capacitación',
    evento: 'Evento',
    devocional: 'Devocional IBA',
  }[tipo] || tipo);

  const handleActividadRSVP = async (id, status, reason) => {
    try {
      await axios.post(
        `${API}/comite/actividades/${id}/rsvp`,
        { status, reason: reason || '' },
        { headers: headersRef.current }
      );
      const msg = { aceptado: '✅ Confirmaste tu asistencia', tal_vez: '🤔 Marcada como tentativa', rechazado: '❌ Marcada como no asistirá' }[status];
      toast.success(msg || 'Respuesta enviada');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo enviar la respuesta');
    }
  };

  if (loading) {
    return <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-rose-600" /></div>;
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-1 bg-gray-100 p-0.5 rounded-lg">
          {['activa', 'completado', 'cancelada'].map((f) => (
            <button
              key={f}
              onClick={() => setFiltro(f)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md capitalize ${
                filtro === f ? 'bg-white shadow-sm text-rose-700' : 'text-gray-600'
              }`}
              data-testid={`comite-actividad-filter-${f}`}
            >
              {f === 'activa' ? 'Activas' : f === 'completado' ? 'Completadas' : 'Canceladas'}
            </button>
          ))}
        </div>
        {perms.puede_crear && (
          <Button onClick={() => { setEditing(null); setShowModal(true); }} size="sm" className="bg-rose-600 hover:bg-rose-700" data-testid="btn-nueva-actividad-comite">
            <Plus className="w-4 h-4 mr-1" /> Nueva actividad
          </Button>
        )}
      </div>

      {filtered.length === 0 ? (
        <Card className="bg-gray-50 border-dashed">
          <CardContent className="p-8 text-center text-gray-500">
            <i className="fas fa-calendar-times text-4xl text-gray-300 mb-2 block"></i>
            <p className="text-sm">No hay actividades en este estado.</p>
          </CardContent>
        </Card>
      ) : (
        <ActividadesSplitGrid
          actividades={filtered}
          onClickCard={setDetalle}
          tipoColor={tipoColor}
          tipoLabel={tipoLabel}
          user={user}
        />
      )}

      {showModal && (
        <ActividadModalComite
          isOpen
          onClose={() => { setShowModal(false); setEditing(null); }}
          onSaved={load}
          API={API}
          editing={editing}
          usuarios={usuariosDisponibles}
        />
      )}

      {detalle && (
        <DetalleActividadComite
          actividad={detalle}
          onClose={() => setDetalle(null)}
          onEdit={() => { setEditing(detalle); setShowModal(true); setDetalle(null); }}
          onCancel={() => handleCancel(detalle.id)}
          onComplete={() => handleEstado(detalle.id, 'completado')}
          onReactivar={() => handleEstado(detalle.id, 'activa')}
          onIniciarTransmision={() => handleIniciarTransmision(detalle.id)}
          onFinalizarTransmision={() => handleFinalizarTransmision(detalle.id)}
          onVerTransmision={() => handleVerTransmision(detalle.id)}
          onRSVP={handleActividadRSVP}
          currentUserId={user?.id}
          currentUserRole={user?.rol}
          tipoLabel={tipoLabel}
          tipoColor={tipoColor}
          canManage={(user?.rol === 'admin') || detalle.creador_id === user?.id}
          API={API}
        />
      )}
    </div>
  );
};

const ActividadesSplitGrid = ({ actividades, onClickCard, tipoColor, tipoLabel, user }) => {
  // Ordenar por fecha_inicio ascendente
  const sorted = useMemo(() => {
    return [...(actividades || [])].sort((x, y) => new Date(x.fecha_inicio) - new Date(y.fecha_inicio));
  }, [actividades]);
  const devocionales = useMemo(() => sorted.filter((a) => a.tipo === 'devocional'), [sorted]);
  const otras = useMemo(() => sorted.filter((a) => a.tipo !== 'devocional'), [sorted]);

  // Encontrar la "próxima" en cada columna (primera con fecha futura)
  const now = Date.now();
  const idxProximaOtras = otras.findIndex((a) => new Date(a.fecha_inicio).getTime() >= now);
  const idxProximaDev = devocionales.findIndex((a) => new Date(a.fecha_inicio).getTime() >= now);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="comite-actividades-split">
      {/* COLUMNA IZQUIERDA — actividades generales */}
      <div className="space-y-3" data-testid="col-otras-actividades">
        <h3 className="font-semibold text-gray-700 text-sm uppercase tracking-wide flex items-center gap-2 sticky top-0 bg-white py-1 z-10">
          <i className="fas fa-star text-rose-500"></i>
          Actividades del Comité
          <span className="text-xs font-normal text-gray-400">({otras.length})</span>
        </h3>
        {otras.length === 0 ? (
          <Card className="bg-gray-50 border-dashed">
            <CardContent className="p-6 text-center text-gray-400 text-sm">
              No hay actividades de comité en este estado.
            </CardContent>
          </Card>
        ) : (
          otras.map((a, i) => (
            <ActividadCard
              key={a.id}
              actividad={a}
              isNext={i === idxProximaOtras}
              onClick={() => onClickCard(a)}
              tipoColor={tipoColor}
              tipoLabel={tipoLabel}
              user={user}
            />
          ))
        )}
      </div>

      {/* COLUMNA DERECHA — devocionales */}
      <div className="space-y-3" data-testid="col-devocionales">
        <h3 className="font-semibold text-indigo-700 text-sm uppercase tracking-wide flex items-center gap-2 sticky top-0 bg-white py-1 z-10">
          <i className="fas fa-bible text-indigo-600"></i>
          Devocionales IBA Uruguay
          <span className="text-xs font-normal text-indigo-400">({devocionales.length})</span>
        </h3>
        {devocionales.length === 0 ? (
          <Card className="bg-indigo-50/30 border-dashed border-indigo-200">
            <CardContent className="p-6 text-center text-indigo-400 text-sm">
              Todavía no hay devocionales programados.
            </CardContent>
          </Card>
        ) : (
          devocionales.map((a, i) => (
            <DevocionalCard
              key={a.id}
              actividad={a}
              isNext={i === idxProximaDev}
              onClick={() => onClickCard(a)}
              user={user}
            />
          ))
        )}
      </div>
    </div>
  );
};

const NextBadge = ({ tone = 'rose' }) => {
  const toneCls = tone === 'indigo'
    ? 'bg-indigo-600'
    : 'bg-rose-600';
  return (
    <div className={`absolute -top-2 left-3 z-10 flex items-center gap-1 ${toneCls} text-white text-[9px] font-bold uppercase px-2 py-0.5 rounded-full shadow-md`}>
      <i className="fas fa-arrow-right"></i> PRÓXIMA
    </div>
  );
};

const ActividadCard = ({ actividad: a, isNext, onClick, tipoColor, tipoLabel, user }) => {
  const fechaIni = new Date(a.fecha_inicio);
  const baseBorder =
    a.estado === 'cancelada' ? 'border-l-red-500 opacity-60' :
    a.estado === 'completado' ? 'border-l-gray-400' :
    'border-l-rose-500';
  const ring = isNext ? 'ring-2 ring-rose-400 ring-offset-2 shadow-lg' : '';
  return (
    <Card
      onClick={onClick}
      className={`hover:shadow-lg transition-shadow cursor-pointer border-l-4 relative ${baseBorder} ${ring}`}
      data-testid={`comite-actividad-card-${a.id}`}
    >
      {isNext && <NextBadge tone="rose" />}
      {a.es_publica && (
        <div className="absolute top-2 left-2 z-10 flex items-center gap-1 bg-emerald-600 text-white text-[9px] font-bold uppercase px-2 py-0.5 rounded-full shadow-md" title="Esta actividad se transmite también en el landing público">
          <i className="fas fa-globe"></i> PÚBLICA
        </div>
      )}
      {a.transmision_activa && (
        <div className="absolute top-2 right-2 z-10 flex items-center gap-1 bg-red-600 text-white text-[10px] font-bold uppercase px-2 py-0.5 rounded-full shadow-md" data-testid={`comite-actividad-live-badge-${a.id}`}>
          <span className="relative flex w-2 h-2">
            <span className="absolute w-2 h-2 bg-white rounded-full animate-ping" />
            <span className="relative w-2 h-2 bg-white rounded-full" />
          </span>
          EN VIVO
        </div>
      )}
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-lg flex items-center min-w-0">
            <i className="fas fa-hands-praying text-rose-500 mr-2 shrink-0"></i>
            <span className="truncate">{a.titulo}</span>
          </CardTitle>
          <Badge className={`${tipoColor(a.tipo)} shrink-0`}>{tipoLabel(a.tipo)}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-gray-600 line-clamp-2 mb-3">{a.descripcion}</p>
        <div className="space-y-1.5 text-sm">
          <div className="flex items-center text-gray-700">
            <i className="fas fa-calendar-alt w-5 text-rose-600"></i>
            <span>{format(fechaIni, "EEEE d 'de' MMMM", { locale: es })}</span>
          </div>
          <div className="flex items-center text-gray-700">
            <i className="fas fa-clock w-5 text-rose-600"></i>
            <span>{format(fechaIni, 'HH:mm', { locale: es })} hrs</span>
          </div>
          {a.ubicacion && (
            <div className="flex items-center text-gray-700">
              <i className="fas fa-map-marker-alt w-5 text-rose-600"></i>
              <span className="truncate">{a.ubicacion}</span>
            </div>
          )}
          {a.encargado_nombre && (
            <div className="flex items-center text-gray-700">
              <i className="fas fa-user-tie w-5 text-rose-600"></i>
              <span className="truncate text-xs">Encargado: <strong>{a.encargado_nombre}</strong></span>
            </div>
          )}
          {a.transmisor_nombre && (
            <div className="flex items-center text-gray-700">
              <i className="fas fa-tower-broadcast w-5 text-rose-600"></i>
              <span className="truncate text-xs">Transmite: <strong>{a.transmisor_nombre}</strong></span>
            </div>
          )}
        </div>
        <div className="mt-3 pt-2 border-t text-xs text-gray-500 flex items-center justify-between gap-2">
          <span>Por: {a.creador_nombre}</span>
          {(a.invitados_ids?.length || 0) > 0 && (
            <span className="flex items-center gap-1.5 text-gray-600">
              <i className="fas fa-users text-rose-500"></i>
              {a.rsvps_resumen?.aceptado || 0}/{(a.invitados_ids || []).length} confirmados
            </span>
          )}
        </div>
        {a.mi_rsvp?.status && a.mi_rsvp.status !== 'pendiente' && a.creador_id !== user?.id && (
          <div className="mt-1.5 text-[10px]">
            <Badge className={`${RSVP_STYLES[a.mi_rsvp.status].bgLight} ${RSVP_STYLES[a.mi_rsvp.status].textColor} border-0`}>
              {RSVP_STYLES[a.mi_rsvp.status].icon} {RSVP_STYLES[a.mi_rsvp.status].label}
            </Badge>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const DevocionalCard = ({ actividad: a, isNext, onClick, user }) => {
  const fechaIni = new Date(a.fecha_inicio);
  const baseClass =
    a.estado === 'cancelada' ? 'border-l-red-500 opacity-60' :
    a.estado === 'completado' ? 'border-l-gray-400' :
    'border-l-indigo-500';
  const ring = isNext ? 'ring-2 ring-indigo-500 ring-offset-2 shadow-xl' : '';
  return (
    <Card
      onClick={onClick}
      className={`hover:shadow-lg transition-shadow cursor-pointer border-l-4 relative bg-gradient-to-br from-indigo-50 via-white to-purple-50 ${baseClass} ${ring}`}
      data-testid={`comite-devocional-card-${a.id}`}
    >
      {isNext && <NextBadge tone="indigo" />}
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base flex items-center min-w-0 text-indigo-900">
            <i className="fas fa-bible text-indigo-600 mr-2 shrink-0"></i>
            <span className="truncate">{a.titulo}</span>
          </CardTitle>
          <Badge className="bg-indigo-100 text-indigo-800 shrink-0 border border-indigo-200">Devocional</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {/* Banner asistencia obligatoria */}
        <div className="bg-amber-100 border border-amber-300 rounded-md px-2.5 py-1.5 flex items-start gap-2" data-testid={`comite-devocional-banner-${a.id}`}>
          <i className="fas fa-triangle-exclamation text-amber-600 mt-0.5 text-sm shrink-0"></i>
          <div className="text-[11px] text-amber-900 font-medium leading-tight">
            Asistencia <strong>obligatoria</strong> para todos los estudiantes — recibirán <strong>calificación por presentismo</strong>.
          </div>
        </div>

        {/* Encargado — destacado */}
        {a.encargado_nombre ? (
          <div className="bg-white border-2 border-indigo-300 rounded-lg p-2.5 flex items-center gap-2.5 shadow-sm" data-testid={`comite-devocional-encargado-${a.id}`}>
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold shrink-0 ring-2 ring-indigo-200">
              {(a.encargado_nombre || '?').slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-indigo-700 uppercase font-bold tracking-wide">Dirige el devocional</p>
              <p className="font-bold text-indigo-900 truncate">{a.encargado_nombre}</p>
            </div>
            <i className="fas fa-crown text-indigo-500 text-lg shrink-0"></i>
          </div>
        ) : (
          <div className="bg-red-50 border border-red-200 rounded-md px-2.5 py-1.5 text-xs text-red-700">
            ⚠ Sin encargado asignado
          </div>
        )}

        <p className="text-xs text-gray-600 line-clamp-2">{a.descripcion}</p>

        <div className="space-y-1 text-sm">
          <div className="flex items-center text-indigo-900">
            <i className="fas fa-calendar-day w-5 text-indigo-600"></i>
            <span className="font-semibold">{format(fechaIni, "EEEE d 'de' MMMM", { locale: es })}</span>
          </div>
          <div className="flex items-center text-gray-700">
            <i className="fas fa-clock w-5 text-indigo-600"></i>
            <span>{format(fechaIni, 'HH:mm', { locale: es })} hrs</span>
          </div>
          {a.ubicacion && (
            <div className="flex items-center text-gray-700 text-xs">
              <i className="fas fa-map-marker-alt w-5 text-indigo-600"></i>
              <span className="truncate">{a.ubicacion}</span>
            </div>
          )}
        </div>

        <div className="pt-2 border-t border-indigo-100 text-xs text-gray-500 flex items-center justify-between gap-2">
          <span className="truncate">Por: {a.creador_nombre}</span>
          {(a.invitados_ids?.length || 0) > 0 && (
            <span className="flex items-center gap-1.5 text-indigo-600">
              <i className="fas fa-users"></i>
              {a.rsvps_resumen?.aceptado || 0}/{(a.invitados_ids || []).length}
            </span>
          )}
        </div>
        {a.mi_rsvp?.status && a.mi_rsvp.status !== 'pendiente' && a.creador_id !== user?.id && (
          <div className="text-[10px]">
            <Badge className={`${RSVP_STYLES[a.mi_rsvp.status].bgLight} ${RSVP_STYLES[a.mi_rsvp.status].textColor} border-0`}>
              {RSVP_STYLES[a.mi_rsvp.status].icon} {RSVP_STYLES[a.mi_rsvp.status].label}
            </Badge>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const ActividadModalComite = ({ isOpen, onClose, onSaved, API, editing, usuarios }) => {
  const today = format(new Date(), 'yyyy-MM-dd');
  const [form, setForm] = useState({
    titulo: editing?.titulo || '',
    descripcion: editing?.descripcion || '',
    fecha_inicio: editing?.fecha_inicio ? format(new Date(editing.fecha_inicio), 'yyyy-MM-dd') : today,
    hora_inicio: editing?.fecha_inicio ? format(new Date(editing.fecha_inicio), 'HH:mm') : '09:00',
    fecha_fin: editing?.fecha_fin ? format(new Date(editing.fecha_fin), 'yyyy-MM-dd') : today,
    hora_fin: editing?.fecha_fin ? format(new Date(editing.fecha_fin), 'HH:mm') : '12:00',
    ubicacion: editing?.ubicacion || '',
    tipo: editing?.tipo || 'evangelismo',
    transmisor_id: editing?.transmisor_id || '',
    encargado_id: editing?.encargado_id || '',
    es_publica: editing?.es_publica || false,
  });
  const [invitados, setInvitados] = useState(new Set(editing?.invitados_ids || []));
  const [invitarTodos, setInvitarTodos] = useState(!editing);
  const [search, setSearch] = useState('');
  const [rolFiltro, setRolFiltro] = useState('todos');
  const [saving, setSaving] = useState(false);
  const headersRef = useRef({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const conteoPorRol = useMemo(() => {
    const acc = { profesor: 0, estudiante: 0, admin: 0, pastor: 0, graduado: 0 };
    (usuarios || []).forEach((p) => { acc[p.rol] = (acc[p.rol] || 0) + 1; });
    return acc;
  }, [usuarios]);

  const usuariosFiltrados = useMemo(() => {
    const q = search.toLowerCase().trim();
    return (usuarios || [])
      .filter((p) => rolFiltro === 'todos' || (p.rol || '').toLowerCase() === rolFiltro)
      .filter((p) => !q || (p.nombre || '').toLowerCase().includes(q) || (p.email || '').toLowerCase().includes(q));
  }, [usuarios, search, rolFiltro]);

  const toggleInv = (id) => {
    setInvitados((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const rolChip = (rol) => {
    const r = (rol || '').toLowerCase();
    if (r === 'profesor') return 'bg-blue-100 text-blue-700 border-blue-300';
    if (r === 'estudiante') return 'bg-emerald-100 text-emerald-700 border-emerald-300';
    if (r === 'admin') return 'bg-purple-100 text-purple-700 border-purple-300';
    if (r === 'pastor') return 'bg-amber-100 text-amber-700 border-amber-300';
    if (r === 'graduado') return 'bg-cyan-100 text-cyan-700 border-cyan-300';
    return 'bg-gray-100 text-gray-600 border-gray-300';
  };

  const save = async () => {
    if (!form.titulo.trim() || !form.descripcion.trim()) {
      toast.error('Título y descripción obligatorios');
      return;
    }
    if (form.tipo === 'devocional' && !form.encargado_id) {
      toast.error('Los devocionales requieren un Encargado asignado');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        titulo: form.titulo.trim(),
        descripcion: form.descripcion.trim(),
        fecha_inicio: `${form.fecha_inicio}T${form.hora_inicio}:00`,
        fecha_fin: `${form.fecha_fin}T${form.hora_fin}:00`,
        ubicacion: form.ubicacion.trim(),
        tipo: form.tipo,
        transmisor_id: form.transmisor_id || null,
        encargado_id: form.encargado_id || null,
        es_publica: form.tipo === 'devocional' ? false : !!form.es_publica,
        invitados: invitarTodos ? [] : Array.from(invitados),
      };
      if (editing) {
        await axios.patch(`${API}/comite/actividades/${editing.id}`, payload, { headers: headersRef.current });
        toast.success('Actividad actualizada');
      } else {
        await axios.post(`${API}/comite/actividades`, payload, { headers: headersRef.current });
        toast.success('Actividad creada — los invitados serán notificados para confirmar');
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center text-rose-700 text-xl">
            <i className="fas fa-hands-praying mr-2"></i>
            {editing ? 'Editar Actividad' : 'Nueva Actividad del Comité'}
          </DialogTitle>
          <DialogDescription>
            Crea actividades evangelísticas, campañas, visitas a iglesias o eventos especiales del comité.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="actv-titulo">Título *</Label>
            <Input id="actv-titulo" value={form.titulo} onChange={(e) => setForm((f) => ({ ...f, titulo: e.target.value }))} placeholder="Ej: Campaña Evangelística en Pocitos" data-testid="comite-actividad-titulo" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="actv-desc">Descripción *</Label>
            <Textarea id="actv-desc" rows={3} value={form.descripcion} onChange={(e) => setForm((f) => ({ ...f, descripcion: e.target.value }))} placeholder="Detalle de la actividad, punto de encuentro, materiales, etc." data-testid="comite-actividad-descripcion" />
          </div>
          <div className="space-y-2">
            <Label>Tipo de actividad</Label>
            <Select value={form.tipo} onValueChange={(v) => setForm((f) => ({ ...f, tipo: v }))}>
              <SelectTrigger data-testid="comite-actividad-tipo"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="evangelismo">Evangelismo</SelectItem>
                <SelectItem value="campana">Campaña</SelectItem>
                <SelectItem value="visita">Visita a Iglesias</SelectItem>
                <SelectItem value="retiro">Retiro</SelectItem>
                <SelectItem value="capacitacion">Capacitación</SelectItem>
                <SelectItem value="evento">Evento General</SelectItem>
                <SelectItem value="devocional">
                  <span className="flex items-center gap-2">
                    <i className="fas fa-bible text-indigo-600"></i>
                    <span className="text-indigo-700 font-semibold">Devocional IBA Uruguay</span>
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
            {form.tipo === 'devocional' && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-md p-2 text-xs text-indigo-800 flex items-start gap-2">
                <i className="fas fa-info-circle mt-0.5"></i>
                <div>
                  <strong>Devocional IBA Uruguay:</strong> requiere asignar un Encargado que dirigirá ese día.
                  La asistencia es <strong>obligatoria para todos los estudiantes</strong> y reciben calificación por presentismo.
                </div>
              </div>
            )}
          </div>
          <div className={`space-y-2 ${form.tipo === 'devocional' ? 'p-3 bg-indigo-50 rounded-lg border border-indigo-200' : ''}`}>
            <Label className="flex items-center gap-2">
              <i className="fas fa-user-tie text-indigo-600"></i>
              Encargado {form.tipo === 'devocional' ? <span className="text-red-600">*</span> : <span className="text-xs text-gray-400 font-normal">(opcional)</span>}
            </Label>
            <Select value={form.encargado_id || 'ninguno'} onValueChange={(v) => setForm((f) => ({ ...f, encargado_id: v === 'ninguno' ? '' : v }))}>
              <SelectTrigger data-testid="comite-actividad-encargado"><SelectValue placeholder={form.tipo === 'devocional' ? 'Seleccioná quién dirigirá el devocional...' : 'Sin encargado asignado'} /></SelectTrigger>
              <SelectContent className="max-h-72">
                {form.tipo !== 'devocional' && <SelectItem value="ninguno">Sin encargado</SelectItem>}
                {usuarios && usuarios.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.nombre} <span className="text-xs text-gray-400">· {u.rol}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.tipo === 'devocional' && (
              <p className="text-[11px] text-indigo-700">
                El nombre del Encargado se mostrará destacado en la tarjeta del devocional para que todos sepan a quién toca dirigir.
              </p>
            )}
          </div>

          {/* === Switch: Transmitir al landing público === */}
          {form.tipo !== 'devocional' ? (
            <div className={`space-y-2 p-3 rounded-lg border ${form.es_publica ? 'border-emerald-300 bg-emerald-50' : 'border-gray-200'}`}>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!form.es_publica}
                  onChange={(e) => setForm((f) => ({ ...f, es_publica: e.target.checked }))}
                  className="w-5 h-5 accent-emerald-600 mt-0.5"
                  data-testid="comite-actividad-es-publica"
                />
                <div className="flex-1">
                  <p className="font-semibold text-sm flex items-center gap-2">
                    <i className="fas fa-globe text-emerald-600"></i>
                    Transmitir al landing público de IBA Connect
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Cuando esta actividad esté en vivo aparecerá en <strong>ibaconnect.info</strong> (fuera del área autenticada), embebida vía YouTube Live. Sirve como <strong>promoción evangelística</strong> — cualquier persona puede verla sin registrarse.
                  </p>
                  {form.es_publica && (
                    <p className="text-[11px] text-emerald-700 mt-1.5">
                      ✅ Esta actividad será pública. El transmisor debe pushear vía RTMP al Stream Key configurado en el canal de YouTube de IBA Uruguay.
                    </p>
                  )}
                </div>
              </label>
            </div>
          ) : (
            <div className="text-xs text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-md p-2">
              <i className="fas fa-lock mr-1"></i>
              Los <strong>Devocionales</strong> son siempre privados — solo visibles para estudiantes y miembros del comité.
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Fecha inicio *</Label>
              <Input type="date" value={form.fecha_inicio} onChange={(e) => setForm((f) => ({ ...f, fecha_inicio: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Hora inicio</Label>
              <Input type="time" value={form.hora_inicio} onChange={(e) => setForm((f) => ({ ...f, hora_inicio: e.target.value }))} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Fecha fin *</Label>
              <Input type="date" value={form.fecha_fin} onChange={(e) => setForm((f) => ({ ...f, fecha_fin: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Hora fin</Label>
              <Input type="time" value={form.hora_fin} onChange={(e) => setForm((f) => ({ ...f, hora_fin: e.target.value }))} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Ubicación</Label>
            <Input value={form.ubicacion} onChange={(e) => setForm((f) => ({ ...f, ubicacion: e.target.value }))} placeholder="Ej: Plaza de los Treinta y Tres" />
          </div>
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <i className="fas fa-tower-broadcast text-rose-600"></i>
              Transmisor asignado <span className="text-xs text-gray-400 font-normal">(opcional — para transmisión en vivo)</span>
            </Label>
            <Select value={form.transmisor_id || 'ninguno'} onValueChange={(v) => setForm((f) => ({ ...f, transmisor_id: v === 'ninguno' ? '' : v }))}>
              <SelectTrigger data-testid="comite-actividad-transmisor"><SelectValue placeholder="Sin transmisor (sin transmisión en vivo)" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="ninguno">Sin transmisor (no se transmitirá)</SelectItem>
                {usuarios && usuarios.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.nombre} <span className="text-xs text-gray-400">· {u.rol}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-gray-500">
              La persona asignada podrá iniciar la transmisión en vivo desde la app. Los estudiantes lejanos se conectarán sólo para ver y escuchar.
            </p>
          </div>

          {/* === Sección Invitados === */}
          <div className="space-y-2 border-t pt-3">
            <Label className="flex items-center gap-2">
              <i className="fas fa-users text-rose-600"></i>
              Asistentes asignados
            </Label>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input
                type="checkbox"
                checked={invitarTodos}
                onChange={(e) => setInvitarTodos(e.target.checked)}
                className="w-4 h-4 accent-rose-600"
                data-testid="comite-actividad-invitar-todos"
              />
              <span className="font-medium text-gray-700">Invitar a todos los miembros del comité + admins</span>
            </label>
            <p className="text-[11px] text-gray-400 ml-6 mt-0.5">
              Desmarcá esto para elegir manualmente a quiénes invitar (estudiantes, profesores, etc.). Los invitados recibirán una notificación y podrán aceptar / rechazar la asistencia.
            </p>
            {!invitarTodos && (
              <div className="mt-2 border border-gray-200 rounded-lg max-h-72 overflow-y-auto" data-testid="comite-actividad-invitados-list">
                <div className="sticky top-0 bg-white px-3 py-2 border-b space-y-2 z-10">
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar por nombre o email..."
                    className="w-full text-sm px-2 py-1 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-rose-500"
                  />
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      { key: 'todos', label: 'Todos', count: usuarios ? usuarios.length : 0, cls: 'bg-gray-100 text-gray-700 border-gray-300' },
                      { key: 'admin', label: 'Admins', count: conteoPorRol.admin || 0, cls: 'bg-purple-100 text-purple-700 border-purple-300' },
                      { key: 'profesor', label: 'Profesores', count: conteoPorRol.profesor || 0, cls: 'bg-blue-100 text-blue-700 border-blue-300' },
                      { key: 'estudiante', label: 'Estudiantes', count: conteoPorRol.estudiante || 0, cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
                      { key: 'pastor', label: 'Pastores', count: conteoPorRol.pastor || 0, cls: 'bg-amber-100 text-amber-700 border-amber-300' },
                      { key: 'graduado', label: 'Graduados', count: conteoPorRol.graduado || 0, cls: 'bg-cyan-100 text-cyan-700 border-cyan-300' },
                    ].filter((c) => c.key === 'todos' || c.count > 0).map((c) => (
                      <button
                        key={c.key}
                        type="button"
                        onClick={() => setRolFiltro(c.key)}
                        className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${rolFiltro === c.key ? `${c.cls} ring-2 ring-offset-1 ring-rose-400` : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                      >
                        {c.label} ({c.count})
                      </button>
                    ))}
                  </div>
                </div>
                {usuariosFiltrados.length === 0 && (
                  <p className="px-3 py-4 text-sm text-gray-400 text-center">Sin resultados</p>
                )}
                {usuariosFiltrados.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0">
                    <input
                      type="checkbox"
                      checked={invitados.has(p.id)}
                      onChange={() => toggleInv(p.id)}
                      className="w-4 h-4 accent-rose-600"
                    />
                    {p.image ? (
                      <img src={p.image} alt={p.nombre} className="w-7 h-7 rounded-full object-cover shrink-0" />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-rose-500 to-amber-500 flex items-center justify-center text-white text-[10px] font-bold shrink-0">
                        {(p.nombre || '?').slice(0, 1).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate flex items-center gap-1.5">
                        <span className="truncate">{p.nombre}</span>
                        <span className={`text-[9px] uppercase font-bold px-1 py-0 rounded border ${rolChip(p.rol)}`}>{p.rol}</span>
                      </p>
                      <p className="text-xs text-gray-400 truncate">{p.email}</p>
                    </div>
                  </label>
                ))}
                <p className="sticky bottom-0 px-3 py-2 text-xs text-gray-500 border-t bg-gray-50">
                  {invitados.size} seleccionado(s)
                </p>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving} className="bg-rose-600 hover:bg-rose-700" data-testid="comite-actividad-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <i className="fas fa-save mr-1"></i>}
            {editing ? 'Actualizar' : 'Crear actividad'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ActividadRsvpBlock = ({ actividad, currentUserId, currentUserRole, onRSVP }) => {
  const isPast = false;
  const miRsvp = actividad.mi_rsvp;
  const invitadosIds = actividad.invitados_ids || [];
  const miStatus = miRsvp?.status || (invitadosIds.includes(currentUserId) ? 'pendiente' : null);
  const soyCreador = actividad.creador_id === currentUserId;
  const puedoRsvp = !isPast && actividad.estado === 'activa' && !soyCreador && !!miStatus;
  const resumen = actividad.rsvps_resumen || {};
  const invitadosConRsvp = actividad.invitados_con_rsvp || [];
  const [showRejectReason, setShowRejectReason] = useState(false);
  const [rejectReason, setRejectReason] = useState(miStatus === 'rechazado' ? (miRsvp?.reason || '') : '');
  const [rsvpSubmitting, setRsvpSubmitting] = useState(false);

  if (invitadosConRsvp.length === 0 && !puedoRsvp) return null;

  const respond = async (status) => {
    if (rsvpSubmitting) return;
    if (status === 'rechazado' && !showRejectReason) {
      setShowRejectReason(true);
      return;
    }
    setRsvpSubmitting(true);
    try {
      await onRSVP(actividad.id, status, status === 'rechazado' ? rejectReason.trim() : '');
      setShowRejectReason(false);
    } finally {
      setRsvpSubmitting(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-3 space-y-3 bg-gray-50" data-testid="comite-actividad-rsvp-block">
      {invitadosConRsvp.length > 0 && (
        <div>
          <div className="flex items-center gap-3 flex-wrap text-xs mb-2">
            <span className="font-semibold text-gray-700">Asistencia ({resumen.total || invitadosConRsvp.length}):</span>
            {resumen.aceptado > 0 && (
              <span className="flex items-center gap-1 text-emerald-700"><span className="w-2 h-2 rounded-full bg-emerald-500" />{resumen.aceptado} asistirá{resumen.aceptado > 1 ? 'n' : ''}</span>
            )}
            {resumen.tal_vez > 0 && (
              <span className="flex items-center gap-1 text-amber-700"><span className="w-2 h-2 rounded-full bg-amber-500" />{resumen.tal_vez} tal vez</span>
            )}
            {resumen.rechazado > 0 && (
              <span className="flex items-center gap-1 text-red-700"><span className="w-2 h-2 rounded-full bg-red-500" />{resumen.rechazado} no podrá{resumen.rechazado > 1 ? 'n' : ''}</span>
            )}
            {resumen.pendiente > 0 && (
              <span className="flex items-center gap-1 text-gray-500"><span className="w-2 h-2 rounded-full bg-gray-400" />{resumen.pendiente} sin responder</span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {invitadosConRsvp.map((inv) => (
              <InvitadoAvatarComite key={inv.id} invitado={inv} />
            ))}
          </div>
        </div>
      )}

      {puedoRsvp && (
        <div className="pt-2 border-t border-gray-200">
          <p className="text-xs font-semibold text-gray-700 mb-2">
            {miStatus === 'pendiente' ? '¿Vas a asistir a esta actividad?' : 'Cambiar mi respuesta:'}
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => respond('aceptado')}
              disabled={rsvpSubmitting}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold ${
                miStatus === 'aceptado' ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200'
              }`}
              data-testid={`comite-actividad-rsvp-accept-${actividad.id}`}
            >
              ✅ Acepto
            </button>
            <button
              onClick={() => respond('tal_vez')}
              disabled={rsvpSubmitting}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold ${
                miStatus === 'tal_vez' ? 'bg-amber-500 text-white' : 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'
              }`}
              data-testid={`comite-actividad-rsvp-maybe-${actividad.id}`}
            >
              🤔 Tal vez
            </button>
            <button
              onClick={() => respond('rechazado')}
              disabled={rsvpSubmitting}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold ${
                miStatus === 'rechazado' ? 'bg-red-600 text-white' : 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200'
              }`}
              data-testid={`comite-actividad-rsvp-reject-${actividad.id}`}
            >
              ❌ No acepto
            </button>
          </div>
          {showRejectReason && (
            <div className="mt-2 space-y-2 bg-red-50 border border-red-200 rounded-md p-2">
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Razón (opcional)..."
                rows={2}
                maxLength={500}
                className="w-full text-xs border border-red-200 rounded p-1.5 focus:outline-none resize-none"
              />
              <div className="flex gap-2 justify-end">
                <button onClick={() => { setShowRejectReason(false); setRejectReason(''); }} className="text-xs text-gray-600 hover:text-gray-800" disabled={rsvpSubmitting}>
                  Cancelar
                </button>
                <button onClick={() => respond('rechazado')} disabled={rsvpSubmitting}
                  className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs font-semibold">
                  {rsvpSubmitting ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Confirmar rechazo'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const DetalleActividadComite = ({ actividad, onClose, onEdit, onCancel, onComplete, onReactivar, onIniciarTransmision, onFinalizarTransmision, onVerTransmision, onRSVP, currentUserId, currentUserRole, tipoLabel, tipoColor, canManage, API }) => {
  const fechaIni = new Date(actividad.fecha_inicio);
  const esTransmisor = actividad.transmisor_id === currentUserId;
  const esAdminOCreador = currentUserRole === 'admin' || actividad.creador_id === currentUserId;
  const puedeEmitir = esTransmisor || esAdminOCreador;
  const enVivo = !!actividad.transmision_activa;

  // Reenvío de invitación a quienes aún no aceptaron.
  const invitadosConRsvp = actividad.invitados_con_rsvp || [];
  const pendientesParaReenvio = invitadosConRsvp.filter((inv) => (inv.status || 'pendiente') !== 'aceptado');
  const [showReenviar, setShowReenviar] = useState(false);
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [reenviando, setReenviando] = useState(false);

  const toggleSel = (id) => {
    setSeleccionados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const seleccionarTodosPendientes = () => {
    setSeleccionados(new Set(pendientesParaReenvio.map((i) => i.id)));
  };
  const limpiarSeleccion = () => setSeleccionados(new Set());

  const handleReenviar = async () => {
    if (seleccionados.size === 0) {
      toast.error('Seleccioná al menos un invitado para reenviar');
      return;
    }
    setReenviando(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const r = await axios.post(
        `${API}/comite/actividades/${actividad.id}/reenviar-invitacion`,
        { user_ids: Array.from(seleccionados) },
        { headers }
      );
      toast.success(`Notificación reenviada a ${r.data?.enviados || seleccionados.size} invitado(s)`);
      setShowReenviar(false);
      setSeleccionados(new Set());
    } catch (e) {
      const msg = e?.response?.data?.detail || 'No se pudo reenviar la invitación';
      toast.error(msg);
    } finally {
      setReenviando(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center text-rose-700">
            <i className="fas fa-hands-praying mr-2"></i>
            {actividad.titulo}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={tipoColor(actividad.tipo)}>{tipoLabel(actividad.tipo)}</Badge>
            {enVivo && (
              <Badge className="bg-red-600 text-white border-0 flex items-center gap-1.5 px-2.5 py-1">
                <span className="relative flex w-2 h-2">
                  <span className="absolute w-2 h-2 bg-white rounded-full animate-ping" />
                  <span className="relative w-2 h-2 bg-white rounded-full" />
                </span>
                EN VIVO
              </Badge>
            )}
          </div>
          <div className="bg-rose-50 p-3 rounded-lg space-y-1 text-sm">
            <p><i className="fas fa-calendar text-rose-600 mr-2"></i><strong>Fecha:</strong> {format(fechaIni, "EEEE d 'de' MMMM, yyyy", { locale: es })}</p>
            <p><i className="fas fa-clock text-rose-600 mr-2"></i><strong>Hora:</strong> {format(fechaIni, 'HH:mm', { locale: es })} hrs</p>
            {actividad.ubicacion && <p><i className="fas fa-map-marker-alt text-rose-600 mr-2"></i><strong>Ubicación:</strong> {actividad.ubicacion}</p>}
            <p><i className="fas fa-user-tie text-rose-600 mr-2"></i><strong>Creado por:</strong> {actividad.creador_nombre}</p>
            {actividad.transmisor_nombre && (
              <p><i className="fas fa-tower-broadcast text-rose-600 mr-2"></i><strong>Transmisor:</strong> {actividad.transmisor_nombre}</p>
            )}
            {actividad.encargado_nombre && (
              <p><i className="fas fa-user-tie text-indigo-600 mr-2"></i><strong>Encargado:</strong> {actividad.encargado_nombre}</p>
            )}
            {actividad.tipo === 'devocional' && (
              <div className="mt-2 bg-amber-100 border border-amber-300 rounded-md px-2 py-1.5 text-xs text-amber-900">
                <i className="fas fa-triangle-exclamation mr-1"></i>
                Asistencia <strong>obligatoria</strong> para todos los estudiantes — recibirán <strong>calificación por presentismo</strong>.
              </div>
            )}
          </div>
          <div>
            <h4 className="font-medium mb-1">Descripción</h4>
            <p className="text-sm text-gray-600 whitespace-pre-wrap">{actividad.descripcion}</p>
          </div>

          {/* === Bloque RSVP + Asistencia === */}
          <ActividadRsvpBlock
            actividad={actividad}
            currentUserId={currentUserId}
            currentUserRole={currentUserRole}
            onRSVP={onRSVP}
          />

          {/* Reenviar invitación: solo admin/creador, si hay invitados que aún no aceptaron */}
          {esAdminOCreador && actividad.estado === 'activa' && pendientesParaReenvio.length > 0 && (
            <div className="border border-blue-200 bg-blue-50 rounded-lg p-3" data-testid="comite-reenviar-block">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-xs text-blue-900">
                  <strong>{pendientesParaReenvio.length}</strong> invitado(s) aún no aceptaron.
                </div>
                <Button
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => { setShowReenviar(true); seleccionarTodosPendientes(); }}
                  data-testid="comite-reenviar-invitacion-btn"
                >
                  <i className="fas fa-paper-plane mr-1"></i> Reenviar notificación
                </Button>
              </div>
            </div>
          )}

          {/* === Bloque de Transmisión en vivo === */}
          {actividad.estado === 'activa' && (
            <div className="border border-rose-200 bg-gradient-to-br from-rose-50 to-amber-50 rounded-lg p-3 space-y-2" data-testid="comite-actividad-transmision-block">
              <h4 className="font-semibold text-sm flex items-center gap-2 text-rose-700">
                <i className="fas fa-tower-broadcast"></i>
                Transmisión en vivo
              </h4>
              {!enVivo && puedeEmitir && (actividad.transmisor_id || esAdminOCreador) && (
                <>
                  <p className="text-xs text-gray-600">
                    Cuando inicies la transmisión, los miembros del comité y administradores recibirán una notificación con un acceso directo para ver y escuchar en vivo.
                  </p>
                  <Button
                    onClick={onIniciarTransmision}
                    size="sm"
                    className="w-full bg-red-600 hover:bg-red-700 text-white"
                    data-testid="comite-actividad-iniciar-transmision"
                  >
                    <span className="relative flex w-2 h-2 mr-2">
                      <span className="absolute w-2 h-2 bg-white rounded-full animate-ping" />
                      <span className="relative w-2 h-2 bg-white rounded-full" />
                    </span>
                    Iniciar transmisión en vivo
                  </Button>
                </>
              )}
              {!enVivo && !puedeEmitir && !actividad.transmisor_id && (
                <p className="text-xs text-gray-500 italic">No hay transmisor asignado a esta actividad.</p>
              )}
              {!enVivo && !puedeEmitir && actividad.transmisor_id && (
                <p className="text-xs text-gray-500">
                  Esperando que <strong>{actividad.transmisor_nombre}</strong> inicie la transmisión.
                </p>
              )}
              {enVivo && (
                <div className="space-y-2">
                  <p className="text-xs text-red-700 font-semibold">
                    🔴 Transmisión EN VIVO ahora
                    {actividad.transmision_iniciada_por_nombre && (
                      <span className="font-normal text-gray-600"> · {actividad.transmision_iniciada_por_nombre}</span>
                    )}
                  </p>
                  {puedeEmitir ? (
                    <div className="flex flex-col gap-2">
                      <Button
                        onClick={onVerTransmision}
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="comite-actividad-reentrar-transmision"
                      >
                        <Video className="w-4 h-4 mr-1" /> Volver a entrar a la sala
                      </Button>
                      <Button
                        onClick={onFinalizarTransmision}
                        size="sm"
                        variant="outline"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        data-testid="comite-actividad-finalizar-transmision"
                      >
                        <i className="fas fa-stop-circle mr-1"></i> Finalizar transmisión
                      </Button>
                    </div>
                  ) : (
                    <Button
                      onClick={onVerTransmision}
                      size="sm"
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="comite-actividad-ver-transmision"
                    >
                      <i className="fas fa-eye mr-2"></i> Ver transmisión en vivo
                    </Button>
                  )}
                  <p className="text-[11px] text-gray-500 italic">
                    Los espectadores entran sin cámara ni micrófono — solo verán y escucharán al transmisor.
                  </p>
                </div>
              )}
            </div>
          )}

          {canManage && actividad.estado === 'activa' && (
            <div className="flex flex-wrap justify-between items-center gap-2 border-t pt-3">
              <Button variant="outline" size="sm" onClick={onEdit} data-testid="comite-actividad-edit">
                <i className="fas fa-edit mr-1"></i> Editar
              </Button>
              {/* Invitar externo: para Devocionales y otras actividades del comité.
                  Para devocionales se permite generar/anticipar el link aunque
                  la sala aún no esté creada (el endpoint la crea al vuelo con
                  el nombre canónico). Para otras actividades solo si ya hay
                  transmisión activa o se generó el room antes. */}
              {(actividad.tipo === 'devocional' || actividad.transmision_room_url) && (
                <Button
                  variant="outline"
                  size="sm"
                  className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                  onClick={async () => {
                    let urlFinal = actividad.transmision_room_url;
                    try {
                      const r = await axios.post(`${API}/comite/actividades/${actividad.id}/renovar-link`, {});
                      if (r?.data?.url) urlFinal = r.data.url;
                    } catch (_e) { /* sigue con URL original */ }
                    const f = actividad.fecha_inicio ? new Date(actividad.fecha_inicio) : null;
                    const fechaStr = f ? `${f.toLocaleDateString('es-UY', { weekday: 'long', day: '2-digit', month: 'long', timeZone: 'America/Montevideo' })} a las ${f.toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Montevideo' })} (hora Uruguay)` : '';
                    const esDevocional = actividad.tipo === 'devocional';
                    const encabezado = esDevocional
                      ? 'Estás invitado al Devocional IBA Uruguay:'
                      : 'Estás invitado a la transmisión del Comité Evangelístico IBA Uruguay:';
                    const titleShare = esDevocional ? `Devocional: ${actividad.titulo}` : `Transmisión: ${actividad.titulo}`;
                    const texto = `${encabezado}\n\n📌 ${actividad.titulo}\n${fechaStr ? `📅 ${fechaStr}\n` : ''}\n🔗 Unirse: ${urlFinal}\n\nNo necesitás cuenta en IBA Connect. Solo abrí el link y poné tu nombre al entrar.`;
                    try {
                      if (navigator.share && /Mobi|Android|iPhone/i.test(navigator.userAgent)) {
                        await navigator.share({ title: titleShare, text: texto });
                        return;
                      }
                      await navigator.clipboard.writeText(texto);
                      toast.success('Link de invitado copiado y renovado.');
                    } catch (_e) {
                      window.prompt('Copiá el siguiente texto para invitar:', texto);
                    }
                  }}
                  data-testid="comite-actividad-invitar-externo"
                >
                  <i className="fas fa-share-alt mr-1"></i> Invitar externo
                </Button>
              )}
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={onComplete} data-testid="comite-actividad-complete">
                <i className="fas fa-flag-checkered mr-1"></i> Cumplida
              </Button>
              <Button size="sm" variant="destructive" onClick={onCancel} data-testid="comite-actividad-cancel">
                <i className="fas fa-ban mr-1"></i> Cancelar
              </Button>
            </div>
          )}
          {canManage && (actividad.estado === 'completado' || actividad.estado === 'cancelada') && (
            <div className="flex justify-end gap-2 border-t pt-3">
              <Button size="sm" className="bg-rose-600 hover:bg-rose-700 text-white" onClick={onReactivar}>
                <i className="fas fa-redo mr-1"></i> Reactivar
              </Button>
            </div>
          )}
        </div>
      </DialogContent>

      {/* Modal de selección para Reenviar invitación */}
      {showReenviar && (
        <Dialog open={showReenviar} onOpenChange={(open) => { if (!open) { setShowReenviar(false); limpiarSeleccion(); } }}>
          <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="comite-reenviar-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center text-blue-700">
                <i className="fas fa-paper-plane mr-2"></i> Reenviar notificación
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <p className="text-sm text-gray-600">
                Seleccioná a quién querés reenviar la invitación. Los que ya aceptaron no aparecen.
              </p>
              <div className="flex justify-between items-center text-xs">
                <span className="font-semibold text-gray-700">{seleccionados.size} de {pendientesParaReenvio.length} seleccionado(s)</span>
                <div className="flex gap-2">
                  <button
                    onClick={seleccionarTodosPendientes}
                    className="text-blue-600 hover:text-blue-800 font-semibold"
                    data-testid="comite-reenviar-seleccionar-todos"
                  >
                    Todos
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    onClick={limpiarSeleccion}
                    className="text-gray-600 hover:text-gray-800 font-semibold"
                    data-testid="comite-reenviar-limpiar"
                  >
                    Ninguno
                  </button>
                </div>
              </div>
              <div className="space-y-1 max-h-[40vh] overflow-y-auto border border-gray-200 rounded-md p-2 bg-gray-50">
                {pendientesParaReenvio.map((inv) => {
                  const sty = RSVP_STYLES[inv.status || 'pendiente'] || RSVP_STYLES.pendiente;
                  const checked = seleccionados.has(inv.id);
                  return (
                    <label
                      key={inv.id}
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-white ${checked ? 'bg-blue-50 border border-blue-300' : 'border border-transparent'}`}
                      data-testid={`comite-reenviar-item-${inv.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleSel(inv.id)}
                        className="w-4 h-4 accent-blue-600"
                      />
                      {inv.image ? (
                        <img src={inv.image} alt={inv.nombre} className="w-7 h-7 rounded-full object-cover" />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-rose-500 to-amber-500 flex items-center justify-center text-white text-xs font-bold">
                          {(inv.nombre || '?').slice(0, 1).toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{inv.nombre}</p>
                        <p className="text-[11px] text-gray-500">
                          <span className={sty.textColor}>{sty.icon} {sty.label}</span>
                          {inv.reason ? ` · ${inv.reason}` : ''}
                        </p>
                      </div>
                    </label>
                  );
                })}
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setShowReenviar(false); limpiarSeleccion(); }}
                  disabled={reenviando}
                  data-testid="comite-reenviar-cancel"
                >
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={handleReenviar}
                  disabled={reenviando || seleccionados.size === 0}
                  data-testid="comite-reenviar-confirm"
                >
                  {reenviando ? (
                    <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Enviando...</>
                  ) : (
                    <><i className="fas fa-paper-plane mr-1"></i> Reenviar a {seleccionados.size}</>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
};

// ============================================================================
// EN VIVO TAB — Lista las actividades del comité que están transmitiendo ahora
// ============================================================================
const EnVivoTabComite = ({ API, user }) => {
  const [transmisiones, setTransmisiones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seccionLandingActiva, setSeccionLandingActiva] = useState(false);
  const [togglingSeccion, setTogglingSeccion] = useState(false);
  const { openCall } = useVideoCall();
  const headersRef = useRef({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/comite/transmisiones-activas`, { headers: headersRef.current });
      setTransmisiones(r.data.transmisiones || []);
    } catch (_e) { /* ignore */ } finally {
      setLoading(false);
    }
    try {
      const s = await axios.get(`${API}/comite/landing-seccion`, { headers: headersRef.current });
      setSeccionLandingActiva(!!s.data.activa);
    } catch (_e) { /* ignore */ }
  }, [API]);

  const toggleSeccionLanding = async () => {
    if (togglingSeccion) return;
    const nuevo = !seccionLandingActiva;
    setTogglingSeccion(true);
    try {
      await axios.patch(`${API}/comite/landing-seccion`, { activa: nuevo }, { headers: headersRef.current });
      setSeccionLandingActiva(nuevo);
      toast.success(nuevo
        ? '🌐 Sección "Comité EN VIVO" ACTIVADA en el landing público'
        : '🔒 Sección oculta del landing público');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al cambiar el estado');
    } finally {
      setTogglingSeccion(false);
    }
  };

  // Poll cada 15 segundos para mantenerse actualizado sin recargar
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const handleVer = async (id) => {
    try {
      const r = await axios.get(`${API}/comite/actividades/${id}/transmision/unirse`, { headers: headersRef.current });
      const url = r.data.es_emisor && r.data.broadcaster_url ? r.data.broadcaster_url : r.data.viewer_url;
      openCall({
        url,
        channelId: null,
        liveChat: r.data.chat_channel_id ? {
          channelId: r.data.chat_channel_id,
          channelType: r.data.chat_channel_type || 'livestream',
        } : null,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo unir a la transmisión');
      load();
    }
  };

  if (loading) {
    return <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-rose-600" /></div>;
  }

  const adminToggle = user?.rol === 'admin' && (
    <Card className={`mb-4 border-2 ${seccionLandingActiva ? 'border-emerald-300 bg-emerald-50' : 'border-gray-200 bg-gray-50'}`} data-testid="comite-landing-toggle-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${seccionLandingActiva ? 'bg-emerald-100' : 'bg-gray-100'}`}>
              {seccionLandingActiva ? '🌐' : '🔒'}
            </div>
            <div>
              <p className="font-semibold text-gray-800">
                Sección pública en Landing
                <Badge className={`ml-2 text-[10px] ${seccionLandingActiva ? 'bg-emerald-500 text-white' : 'bg-gray-300 text-gray-700'} border-0`}>
                  {seccionLandingActiva ? 'ACTIVADA' : 'DESACTIVADA'}
                </Badge>
              </p>
              <p className="text-xs text-gray-600 mt-0.5">
                {seccionLandingActiva
                  ? 'La sección "Comité EN VIVO" aparece en ibaconnect.info aunque no haya transmisión activa (modo "Próximamente").'
                  : 'La sección NO aparece en el landing público. Actívala el día de la transmisión.'}
              </p>
            </div>
          </div>
          <button
            onClick={toggleSeccionLanding}
            disabled={togglingSeccion}
            className={`px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2 transition-colors ${
              seccionLandingActiva
                ? 'bg-red-100 hover:bg-red-200 text-red-700 border border-red-300'
                : 'bg-emerald-600 hover:bg-emerald-700 text-white'
            }`}
            data-testid="comite-landing-toggle-btn"
          >
            {togglingSeccion ? <Loader2 className="w-4 h-4 animate-spin" /> : seccionLandingActiva ? <i className="fas fa-eye-slash" /> : <i className="fas fa-eye" />}
            {seccionLandingActiva ? 'Apagar' : 'Activar sección'}
          </button>
        </div>
      </CardContent>
    </Card>
  );

  if (transmisiones.length === 0) {
    return (
      <div>
        {adminToggle}
        <Card className="bg-gray-50 border-dashed" data-testid="comite-en-vivo-empty">
          <CardContent className="p-10 text-center text-gray-500">
            <div className="relative inline-flex items-center justify-center w-16 h-16 mb-3">
              <i className="fas fa-tower-broadcast text-4xl text-gray-300"></i>
            </div>
            <p className="text-base font-medium text-gray-700">No hay transmisiones en vivo en este momento</p>
            <p className="text-sm mt-1">
              Cuando un miembro del comité inicie una transmisión, va a aparecer acá automáticamente.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="comite-en-vivo-list">
      {adminToggle}
      <div className="flex items-center gap-2 mb-2">
        <span className="relative flex w-3 h-3">
          <span className="absolute w-3 h-3 bg-red-500 rounded-full animate-ping" />
          <span className="relative w-3 h-3 bg-red-500 rounded-full" />
        </span>
        <span className="font-semibold text-red-600">{transmisiones.length} transmisión{transmisiones.length === 1 ? '' : 'es'} en vivo</span>
      </div>
      {transmisiones.map((t) => {
        const fecha = t.fecha_inicio ? new Date(t.fecha_inicio) : null;
        const ini = t.transmision_iniciada_at ? new Date(t.transmision_iniciada_at) : null;
        return (
          <Card key={t.id} className="border-2 border-red-300 bg-gradient-to-br from-red-50 via-white to-amber-50 shadow-md" data-testid={`comite-en-vivo-card-${t.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <Badge className="bg-red-600 text-white border-0 flex items-center gap-1 px-2 py-0.5">
                      <span className="relative flex w-2 h-2">
                        <span className="absolute w-2 h-2 bg-white rounded-full animate-ping" />
                        <span className="relative w-2 h-2 bg-white rounded-full" />
                      </span>
                      EN VIVO
                    </Badge>
                    <h3 className="font-semibold text-gray-800 truncate">{t.titulo}</h3>
                  </div>
                  <p className="text-xs text-gray-500">
                    <i className="fas fa-tower-broadcast text-rose-600 mr-1"></i>
                    Transmite: <strong>{t.transmision_iniciada_por_nombre || t.transmisor_nombre || 'Sin nombre'}</strong>
                  </p>
                  {ini && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      Iniciada: {ini.toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Montevideo' })}
                    </p>
                  )}
                  {t.ubicacion && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      <i className="fas fa-map-marker-alt text-rose-600 mr-1"></i> {t.ubicacion}
                    </p>
                  )}
                  {fecha && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      Actividad: {format(fecha, "d MMM, HH:mm", { locale: es })}
                    </p>
                  )}
                </div>
                <Button
                  onClick={() => handleVer(t.id)}
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 shrink-0"
                  data-testid={`comite-en-vivo-ver-${t.id}`}
                >
                  <i className="fas fa-eye mr-1.5"></i>
                  {(t.transmisor_id === user?.id || t.transmision_iniciada_por_id === user?.id || user?.rol === 'admin') ? 'Volver a entrar' : 'Ver en vivo'}
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
      <p className="text-[11px] text-gray-400 text-center mt-2 italic">
        Como espectador entrás sin cámara ni micrófono. Solo vas a ver y escuchar al transmisor.
      </p>
    </div>
  );
};

// ============================================================================
// MIEMBROS TAB — Solo admin gestiona; cualquiera ve la lista
// ============================================================================
const MiembrosTabComite = ({ API, user, perms }) => {
  const [miembros, setMiembros] = useState([]);
  const [usuariosDisponibles, setUsuariosDisponibles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPicker, setShowPicker] = useState(false);
  const [editCargoMiembro, setEditCargoMiembro] = useState(null);
  const headersRef = useRef({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/comite/miembros`, { headers: headersRef.current });
      setMiembros(r.data || []);
      if (perms.es_admin) {
        try {
          const pRes = await axios.get(`${API}/comite/disponibles`, { headers: headersRef.current });
          setUsuariosDisponibles(pRes.data.usuarios || []);
        } catch (_e) { /* ignore */ }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [API, perms.es_admin]);

  useEffect(() => { load(); }, [load]);

  const handleAddBatch = async (userIds) => {
    try {
      const r = await axios.post(
        `${API}/comite/miembros`,
        { user_ids: userIds },
        { headers: headersRef.current }
      );
      const { added_count, skipped_count } = r.data;
      if (added_count > 0) {
        toast.success(`✅ ${added_count} miembro${added_count === 1 ? '' : 's'} agregado${added_count === 1 ? '' : 's'} al comité${skipped_count ? ` (${skipped_count} ya estaban)` : ''}`);
      } else if (skipped_count > 0) {
        toast.info('Esos usuarios ya eran miembros del comité');
      }
      setShowPicker(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo agregar');
    }
  };

  const handleRemove = async (userId, nombre) => {
    if (!window.confirm(`¿Quitar a "${nombre}" del comité?`)) return;
    try {
      await axios.delete(`${API}/comite/miembros/${userId}`, { headers: headersRef.current });
      toast.success('Miembro quitado del comité');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    }
  };

  const handleSetCargo = async (userId, nombre, nuevoCargo) => {
    try {
      await axios.patch(
        `${API}/comite/miembros/${userId}`,
        { cargo: nuevoCargo },
        { headers: headersRef.current }
      );
      const msg = nuevoCargo === 'encargado' ? `👑 ${nombre} fue designado Encargado del Comité`
        : nuevoCargo === 'secretario' ? `📝 ${nombre} fue designado Secretario del Comité`
        : `${nombre} ahora es miembro regular`;
      toast.success(msg);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    }
  };

  // Estadísticas por rol + cargo destacado
  const stats = useMemo(() => {
    const s = { total: miembros.length, admin: 0, profesor: 0, estudiante: 0, pastor: 0, graduado: 0, otros: 0 };
    miembros.forEach((m) => {
      const r = (m.rol || '').toLowerCase();
      if (s[r] !== undefined) s[r] += 1; else s.otros += 1;
    });
    return s;
  }, [miembros]);

  const encargado = useMemo(() => miembros.find((m) => m.cargo === 'encargado'), [miembros]);
  const secretario = useMemo(() => miembros.find((m) => m.cargo === 'secretario'), [miembros]);

  // IDs de usuarios YA miembros (para excluir del picker)
  const idsYaMiembros = useMemo(() => new Set(miembros.map((m) => m.user_id)), [miembros]);

  const rolChipClass = (rol) => {
    const r = (rol || '').toLowerCase();
    if (r === 'profesor') return 'bg-blue-100 text-blue-700';
    if (r === 'estudiante') return 'bg-emerald-100 text-emerald-700';
    if (r === 'admin') return 'bg-purple-100 text-purple-700';
    if (r === 'pastor') return 'bg-amber-100 text-amber-700';
    if (r === 'graduado') return 'bg-cyan-100 text-cyan-700';
    return 'bg-gray-100 text-gray-600';
  };

  if (loading) {
    return <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-rose-600" /></div>;
  }

  return (
    <div>
      {/* Header: explicación + stats + botón agregar */}
      <Card className="mb-4 bg-gradient-to-br from-rose-50 to-amber-50 border-rose-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <i className="fas fa-users text-rose-600 text-2xl mt-1"></i>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-800 mb-1">¿Qué es el Comité Evangelístico?</h3>
              <p className="text-xs text-gray-600 mb-3">
                Los miembros del comité pueden <strong>crear y gestionar</strong> reuniones y actividades evangelísticas (sin necesidad de ser admin). Cuando se crea una reunión sin invitados específicos, se invita automáticamente a <strong>todos los miembros</strong>.
              </p>
              <div className="flex flex-wrap gap-1.5">
                <Badge className="bg-white text-gray-700 border border-gray-200">
                  <i className="fas fa-users mr-1"></i> {stats.total} miembro{stats.total === 1 ? '' : 's'}
                </Badge>
                {encargado && (
                  <Badge className="bg-yellow-100 text-yellow-800 border border-yellow-300">
                    <i className="fas fa-crown mr-1"></i> Encargado: {encargado.nombre}
                  </Badge>
                )}
                {secretario && (
                  <Badge className="bg-indigo-100 text-indigo-800 border border-indigo-300">
                    <i className="fas fa-feather-pointed mr-1"></i> Secretario: {secretario.nombre}
                  </Badge>
                )}
                {stats.admin > 0 && <Badge className="bg-purple-100 text-purple-700 border-0">{stats.admin} admin{stats.admin === 1 ? '' : 's'}</Badge>}
                {stats.profesor > 0 && <Badge className="bg-blue-100 text-blue-700 border-0">{stats.profesor} profesor{stats.profesor === 1 ? '' : 'es'}</Badge>}
                {stats.estudiante > 0 && <Badge className="bg-emerald-100 text-emerald-700 border-0">{stats.estudiante} estudiante{stats.estudiante === 1 ? '' : 's'}</Badge>}
                {stats.pastor > 0 && <Badge className="bg-amber-100 text-amber-700 border-0">{stats.pastor} pastor{stats.pastor === 1 ? '' : 'es'}</Badge>}
                {stats.graduado > 0 && <Badge className="bg-cyan-100 text-cyan-700 border-0">{stats.graduado} graduado{stats.graduado === 1 ? '' : 's'}</Badge>}
                {stats.otros > 0 && <Badge className="bg-gray-100 text-gray-600 border-0">{stats.otros} otros</Badge>}
              </div>
            </div>
            {perms.es_admin && (
              <Button onClick={() => setShowPicker(true)} size="sm" className="bg-rose-600 hover:bg-rose-700 shrink-0" data-testid="btn-add-miembro-comite">
                <UserPlus className="w-4 h-4 mr-1" /> Agregar
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Lista de miembros */}
      {miembros.length === 0 ? (
        <Card className="bg-gray-50 border-dashed">
          <CardContent className="p-8 text-center text-gray-500">
            <Users className="w-10 h-10 mx-auto text-gray-300 mb-2" />
            <p className="text-sm">Todavía no hay miembros en el comité.</p>
            {perms.es_admin && (
              <Button onClick={() => setShowPicker(true)} size="sm" className="mt-3 bg-rose-600 hover:bg-rose-700">
                <UserPlus className="w-4 h-4 mr-1" /> Agregar primer miembro
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {miembros.map((m) => {
            const cargo = m.cargo || 'miembro';
            const esEncargado = cargo === 'encargado';
            const esSecretario = cargo === 'secretario';
            const cardClass = esEncargado
              ? 'border-2 border-yellow-400 shadow-md bg-gradient-to-br from-yellow-50 to-amber-50'
              : esSecretario
                ? 'border-2 border-indigo-400 shadow-md bg-gradient-to-br from-indigo-50 to-purple-50'
                : 'border-gray-100';
            return (
              <Card key={m.id} className={`${cardClass} hover:shadow-md transition-shadow relative overflow-visible`} data-testid={`comite-miembro-${m.user_id}`}>
                {esEncargado && (
                  <Badge className="absolute -top-2 left-3 bg-yellow-500 text-white border-0 text-[10px] shadow-md" data-testid={`comite-miembro-cargo-${m.user_id}`}>
                    <i className="fas fa-crown mr-1"></i> ENCARGADO
                  </Badge>
                )}
                {esSecretario && (
                  <Badge className="absolute -top-2 left-3 bg-indigo-500 text-white border-0 text-[10px] shadow-md" data-testid={`comite-miembro-cargo-${m.user_id}`}>
                    <i className="fas fa-feather-pointed mr-1"></i> SECRETARIO
                  </Badge>
                )}
                <CardContent className="p-3 flex items-center gap-3">
                  {m.image ? (
                    <img src={m.image} alt={m.nombre} className={`w-11 h-11 rounded-full object-cover shrink-0 ${esEncargado ? 'ring-2 ring-yellow-400' : esSecretario ? 'ring-2 ring-indigo-400' : ''}`} />
                  ) : (
                    <div className={`w-11 h-11 rounded-full bg-gradient-to-br from-rose-500 to-amber-500 flex items-center justify-center text-white font-bold shrink-0 ${esEncargado ? 'ring-2 ring-yellow-400' : esSecretario ? 'ring-2 ring-indigo-400' : ''}`}>
                      {(m.nombre || m.email || '?').slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-gray-800 truncate">{m.nombre}</p>
                    <p className="text-xs text-gray-400 truncate">{m.email}</p>
                    <Badge className={`${rolChipClass(m.rol)} border-0 text-[10px] mt-0.5`}>{m.rol || 'miembro'}</Badge>
                  </div>
                  {perms.es_admin && (
                    <div className="flex flex-col gap-1 shrink-0">
                      <button
                        onClick={() => setEditCargoMiembro(m)}
                        className="text-gray-400 hover:text-rose-600 p-1.5 rounded hover:bg-rose-50"
                        title="Editar cargo"
                        data-testid={`comite-edit-miembro-${m.user_id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleRemove(m.user_id, m.nombre)}
                        className="text-gray-400 hover:text-red-500 p-1.5 rounded hover:bg-red-50"
                        title="Quitar del comité"
                        data-testid={`comite-remove-miembro-${m.user_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {showPicker && (
        <MiembrosPickerModal
          isOpen
          onClose={() => setShowPicker(false)}
          onConfirm={handleAddBatch}
          usuarios={usuariosDisponibles}
          excludeIds={idsYaMiembros}
        />
      )}

      {editCargoMiembro && (
        <EditCargoModal
          miembro={editCargoMiembro}
          onClose={() => setEditCargoMiembro(null)}
          onSave={async (cargo) => {
            await handleSetCargo(editCargoMiembro.user_id, editCargoMiembro.nombre, cargo);
            setEditCargoMiembro(null);
          }}
          encargadoActual={encargado}
          secretarioActual={secretario}
        />
      )}
    </div>
  );
};

const MiembrosPickerModal = ({ isOpen, onClose, onConfirm, usuarios, excludeIds }) => {
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [rolFiltro, setRolFiltro] = useState('todos');
  const [submitting, setSubmitting] = useState(false);

  const candidates = useMemo(() => {
    return usuarios.filter((u) => !excludeIds.has(u.id));
  }, [usuarios, excludeIds]);

  const conteoPorRol = useMemo(() => {
    const acc = { profesor: 0, estudiante: 0, admin: 0, pastor: 0, graduado: 0 };
    candidates.forEach((p) => { acc[p.rol] = (acc[p.rol] || 0) + 1; });
    return acc;
  }, [candidates]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return candidates
      .filter((p) => rolFiltro === 'todos' || (p.rol || '').toLowerCase() === rolFiltro)
      .filter((p) => !q || (p.nombre || '').toLowerCase().includes(q) || (p.email || '').toLowerCase().includes(q));
  }, [candidates, search, rolFiltro]);

  const toggle = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const submit = async () => {
    if (selected.size === 0) return;
    setSubmitting(true);
    try {
      await onConfirm(Array.from(selected));
    } finally {
      setSubmitting(false);
    }
  };

  const rolChip = (rol) => {
    const r = (rol || '').toLowerCase();
    if (r === 'profesor') return 'bg-blue-100 text-blue-700 border-blue-300';
    if (r === 'estudiante') return 'bg-emerald-100 text-emerald-700 border-emerald-300';
    if (r === 'admin') return 'bg-purple-100 text-purple-700 border-purple-300';
    if (r === 'pastor') return 'bg-amber-100 text-amber-700 border-amber-300';
    if (r === 'graduado') return 'bg-cyan-100 text-cyan-700 border-cyan-300';
    return 'bg-gray-100 text-gray-600 border-gray-300';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="comite-miembros-picker-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        <div className="bg-gradient-to-r from-rose-600 to-amber-600 text-white px-5 py-3 flex items-center justify-between">
          <h3 className="font-semibold flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            Agregar miembros al comité
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-white/15 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-3 border-b bg-gray-50">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre o email..."
            className="w-full text-sm px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500"
            data-testid="comite-miembros-search"
          />
          <div className="flex flex-wrap gap-1.5">
            {[
              { key: 'todos', label: 'Todos', count: candidates.length, cls: 'bg-gray-100 text-gray-700 border-gray-300' },
              { key: 'admin', label: 'Admins', count: conteoPorRol.admin || 0, cls: 'bg-purple-100 text-purple-700 border-purple-300' },
              { key: 'profesor', label: 'Profesores', count: conteoPorRol.profesor || 0, cls: 'bg-blue-100 text-blue-700 border-blue-300' },
              { key: 'estudiante', label: 'Estudiantes', count: conteoPorRol.estudiante || 0, cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
              { key: 'pastor', label: 'Pastores', count: conteoPorRol.pastor || 0, cls: 'bg-amber-100 text-amber-700 border-amber-300' },
              { key: 'graduado', label: 'Graduados', count: conteoPorRol.graduado || 0, cls: 'bg-cyan-100 text-cyan-700 border-cyan-300' },
            ].filter((c) => c.key === 'todos' || c.count > 0).map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => setRolFiltro(c.key)}
                className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${rolFiltro === c.key ? `${c.cls} ring-2 ring-offset-1 ring-rose-400` : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                data-testid={`comite-miembros-filter-${c.key}`}
              >
                {c.label} ({c.count})
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-400">
              {candidates.length === 0
                ? 'No hay más usuarios para agregar — ya están todos en el comité.'
                : 'Sin resultados con ese filtro.'}
            </div>
          ) : (
            filtered.map((p) => (
              <label key={p.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-rose-50 cursor-pointer border-b border-gray-100 last:border-0">
                <input
                  type="checkbox"
                  checked={selected.has(p.id)}
                  onChange={() => toggle(p.id)}
                  className="w-4 h-4 accent-rose-600 shrink-0"
                  data-testid={`comite-miembros-check-${p.id}`}
                />
                {p.image ? (
                  <img src={p.image} alt={p.nombre} className="w-9 h-9 rounded-full object-cover shrink-0" />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-rose-500 to-amber-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
                    {(p.nombre || '?').slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate flex items-center gap-1.5">
                    <span className="truncate">{p.nombre}</span>
                    <span className={`text-[9px] uppercase font-bold px-1 py-0 rounded border ${rolChip(p.rol)}`}>{p.rol}</span>
                  </p>
                  <p className="text-xs text-gray-400 truncate">{p.email}</p>
                </div>
              </label>
            ))
          )}
        </div>
        <div className="border-t bg-white px-5 py-3 flex items-center justify-between gap-2">
          <p className="text-xs text-gray-500">
            <strong className="text-rose-600">{selected.size}</strong> seleccionado{selected.size === 1 ? '' : 's'}
          </p>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
              Cancelar
            </button>
            <Button
              onClick={submit}
              disabled={submitting || selected.size === 0}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="comite-miembros-confirm"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <UserPlus className="w-4 h-4 mr-1" />}
              Agregar {selected.size > 0 ? `(${selected.size})` : ''}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const EditCargoModal = ({ miembro, onClose, onSave, encargadoActual, secretarioActual }) => {
  const [cargo, setCargo] = useState(miembro.cargo || 'miembro');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (cargo === (miembro.cargo || 'miembro')) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      await onSave(cargo);
    } finally {
      setSaving(false);
    }
  };

  const willReplaceEncargado = cargo === 'encargado' && encargadoActual && encargadoActual.user_id !== miembro.user_id;
  const willReplaceSecretario = cargo === 'secretario' && secretarioActual && secretarioActual.user_id !== miembro.user_id;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center text-rose-700">
            <Edit2 className="w-5 h-5 mr-2" />
            Editar cargo de {miembro.nombre}
          </DialogTitle>
          <DialogDescription>
            Asigná un cargo dentro del comité. Sólo puede haber UN encargado y UN secretario a la vez.
          </DialogDescription>
        </DialogHeader>
        <div className="py-3 space-y-2">
          <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${cargo === 'encargado' ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200 hover:bg-gray-50'}`}>
            <input type="radio" name="cargo" checked={cargo === 'encargado'} onChange={() => setCargo('encargado')} className="accent-yellow-500" data-testid="comite-cargo-encargado" />
            <i className="fas fa-crown text-yellow-500 text-lg"></i>
            <div className="flex-1">
              <p className="font-semibold text-sm">Encargado</p>
              <p className="text-xs text-gray-500">Líder del comité — coordina actividades y reuniones.</p>
            </div>
          </label>
          <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${cargo === 'secretario' ? 'border-indigo-400 bg-indigo-50' : 'border-gray-200 hover:bg-gray-50'}`}>
            <input type="radio" name="cargo" checked={cargo === 'secretario'} onChange={() => setCargo('secretario')} className="accent-indigo-500" data-testid="comite-cargo-secretario" />
            <i className="fas fa-feather-pointed text-indigo-500 text-lg"></i>
            <div className="flex-1">
              <p className="font-semibold text-sm">Secretario</p>
              <p className="text-xs text-gray-500">Lleva actas y registros de las reuniones del comité.</p>
            </div>
          </label>
          <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${cargo === 'miembro' ? 'border-rose-400 bg-rose-50' : 'border-gray-200 hover:bg-gray-50'}`}>
            <input type="radio" name="cargo" checked={cargo === 'miembro'} onChange={() => setCargo('miembro')} className="accent-rose-500" data-testid="comite-cargo-miembro" />
            <i className="fas fa-user text-rose-500 text-lg"></i>
            <div className="flex-1">
              <p className="font-semibold text-sm">Miembro</p>
              <p className="text-xs text-gray-500">Miembro regular del comité, sin cargo especial.</p>
            </div>
          </label>
          {(willReplaceEncargado || willReplaceSecretario) && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-2 text-xs text-amber-800">
              ⚠️ {willReplaceEncargado && <>El cargo de Encargado pasará de <strong>{encargadoActual.nombre}</strong> a <strong>{miembro.nombre}</strong>. El anterior quedará como miembro regular.</>}
              {willReplaceSecretario && <>El cargo de Secretario pasará de <strong>{secretarioActual.nombre}</strong> a <strong>{miembro.nombre}</strong>. El anterior quedará como miembro regular.</>}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} className="bg-rose-600 hover:bg-rose-700" data-testid="comite-cargo-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <i className="fas fa-save mr-1"></i>}
            Guardar cargo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ComiteEvangelistico;
